// Ported from Claude Design Nymbl Vyllage world.js — isometric pixel office engine.
// Registers <vyllage-world> and <vyllage-avatar> custom elements.
import { ACTION_SHORTCUT_MAP, getOfficeAction } from "@/lib/office/actions";
import {
  cloneMapLayout,
  DEFAULT_MAP_LAYOUT,
  isMapFurnitureKind,
  MAP_COLS,
  MAP_ROWS,
} from "@/lib/office/personal-office-layout";
import { computeSpatialVolume } from "@/lib/office/spatial-volume";
import {
  buildingDrawScale,
  collectEssentialSpriteSrcs,
  collectLayoutSpriteSrcs,
  ensureSprite,
  FLOOR_TILES,
  footprintCenter,
  furniturePlacementDefaults,
  measureContentHeight,
  OUTDOOR_FLOOR_BY_RUG,
  proceduralHeightPx,
  resolveFurnitureSprite,
  spriteDrawRect,
  spriteDrawRectForFootprint,
  spriteScaleFor,
  warmSprites,
} from "@/lib/office/sprites";

export function registerVyllageElements() {
  if (customElements.get("vyllage-world")) return;
  const TW = 64,
    TH = 32,
    COLS = MAP_COLS,
    ROWS = MAP_ROWS,
    OX = (ROWS * TW) / 2;
  const WORLD_W = ((COLS + ROWS) * TW) / 2,
    WORLD_H = ((COLS + ROWS) * TH) / 2;
  const MTOP = 150,
    MBOT = 90;
  const P = {
    navy: "#001847",
    navyDeep: "#000F2E",
    cyan: "#2D9BD6",
    blue: "#407EC1",
    indigo: "#5461AD",
    purple: "#64479D",
    cta: "#1E6CC7",
    sky: "#E8F2FB",
    paper: "#FAFBFD",
    mist: "#F2F5F9",
    cloud: "#E6EBF2",
    stone: "#C8D0DC",
    slate: "#8894A8",
    graphite: "#4A5568",
    ink: "#1F2A44",
    green: "#2EA86A",
    amber: "#E8A93C",
    red: "#D8443C",
    white: "#FFFFFF",
  };
  const STATUS_C = { available: "#2EA86A", focus: "#64479D", meeting: "#1E6CC7", away: "#8894A8" };
  const iso = (gx, gy) => ({ x: ((gx - gy) * TW) / 2 + OX, y: ((gx + gy) * TH) / 2 + MTOP });
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const shade = (hex, amt) => {
    const n = parseInt(hex.slice(1), 16);
    const r = clamp(((n >> 16) & 255) + amt, 0, 255),
      g = clamp(((n >> 8) & 255) + amt, 0, 255),
      b = clamp((n & 255) + amt, 0, 255);
    return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
  };

  // Runtime door / plaque / decor state keyed by slotKey (set via setPersonalOffices).
  const personalOfficeState = new Map();

  /** Active published (or default) map geometry — rebuilt via applyMapLayout. */
  let currentLayout = cloneMapLayout(DEFAULT_MAP_LAYOUT);
  let officeSlots = currentLayout.offices;
  let commonZones = currentLayout.commonZones;
  const ZONES = [];

  const zoneAt = (gx, gy) => {
    const z = ZONES.find((zz) => gx >= zz.x1 && gx <= zz.x2 && gy >= zz.y1 && gy <= zz.y2);
    if (!z) return { id: "commons", name: "The Commons" };
    if (z.personal) {
      const st = personalOfficeState.get(z.id);
      return {
        ...z,
        name: st?.displayName || z.name,
        doorStatus: st?.doorStatus || "closed",
        ownerUserId: st?.ownerUserId ?? null,
      };
    }
    return z;
  };

  // ---- grid ----
  const walk = new Uint8Array(COLS * ROWS).fill(1);
  const seats = {}; // "x,y" -> {dir}
  const block = (x, y) => {
    if (x >= 0 && y >= 0 && x < COLS && y < ROWS) walk[y * COLS + x] = 0;
  };
  const seat = (x, y, dir) => {
    seats[x + "," + y] = { dir, occ: null };
  };
  const isWalk = (x, y, asTarget) => {
    if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
    if (!walk[y * COLS + x]) return false;
    return true;
  };

  const furn = []; // {gx,gy,w,d,kind,dir,key}
  const F = (kind, gx, gy, w, d, dir, opts) => {
    furn.push(Object.assign({ kind, gx, gy, w: w || 1, d: d || 1, dir: dir || "S" }, opts || {}));
  };

  /** Build a private room (boardroom / huddle) with doors on south + west. */
  function buildPrivateRoom(zone, opts) {
    const doors = opts?.doors || [];
    for (let x = zone.x1; x <= zone.x2; x++) {
      block(x, zone.y1);
      block(x, zone.y2);
    }
    for (let y = zone.y1; y <= zone.y2; y++) {
      block(zone.x1, y);
      block(zone.x2, y);
    }
    for (const d of doors) {
      if (d.gx >= 0 && d.gy >= 0) walk[d.gy * COLS + d.gx] = 1;
    }
    for (let x = zone.x1; x <= zone.x2; x++) {
      F("wall", x, zone.y1, 1, 1, "S", {
        scr: opts?.screen && x >= opts.screen[0] && x <= opts.screen[1],
      });
      const isDoor = doors.some((d) => d.gx === x && d.gy === zone.y2);
      if (!isDoor) F("gwall", x, zone.y2, 1, 1);
    }
    for (let y = zone.y1 + 1; y < zone.y2; y++) {
      F("gwallW", zone.x2, y, 1, 1);
      const isDoor = doors.some((d) => d.gx === zone.x1 && d.gy === y);
      if (!isDoor) F("gwallW", zone.x1, y, 1, 1);
    }
  }

  // ── Center amenity zones (driven by commonZones + roomKind) ──────
  function furnishMeetingRoom(zone) {
    const midX = Math.floor((zone.x1 + zone.x2) / 2);
    const midY = Math.floor((zone.y1 + zone.y2) / 2);
    // Wide south door + side door so large groups can flow in.
    buildPrivateRoom(zone, {
      doors: [
        { gx: midX - 2, gy: zone.y2 },
        { gx: midX + 2, gy: zone.y2 },
        { gx: zone.x1, gy: midY },
        { gx: zone.x2, gy: midY },
      ],
      screen: [midX - 2, midX + 3],
    });
    // Long conference table (6×2) for all-hands — procedural multi-tile so chairs
    // sort correctly around it (sprite billboards bury far-side seats).
    const tx = midX - 2;
    const ty = midY - 1;
    F("conftable", tx, ty, 6, 2);
    for (let x = tx; x < tx + 6; x++) {
      block(x, ty);
      block(x, ty + 1);
    }
    // Seats on both long edges (facing table) + head/foot
    for (let x = tx; x < tx + 6; x++) {
      F("chair", x, ty - 1, 1, 1, "S");
      seat(x, ty - 1, "S");
      F("chair", x, ty + 2, 1, 1, "N");
      seat(x, ty + 2, "N");
    }
    // End seats centered on the two table rows
    F("chair", tx - 1, ty, 1, 1, "E");
    seat(tx - 1, ty, "E");
    F("chair", tx - 1, ty + 1, 1, 1, "E");
    seat(tx - 1, ty + 1, "E");
    F("chair", tx + 6, ty, 1, 1, "W");
    seat(tx + 6, ty, "W");
    F("chair", tx + 6, ty + 1, 1, 1, "W");
    seat(tx + 6, ty + 1, "W");
    // Gallery seats along south wall (skip table aisle)
    for (let x = zone.x1 + 2; x <= zone.x2 - 2; x += 2) {
      if (x >= tx - 1 && x <= tx + 6) continue;
      F("chair", x, zone.y2 - 1, 1, 1, "N");
      seat(x, zone.y2 - 1, "N");
    }
    F("plant", zone.x2 - 1, zone.y1 + 1);
    block(zone.x2 - 1, zone.y1 + 1);
    F("plant", zone.x1 + 1, zone.y1 + 1);
    block(zone.x1 + 1, zone.y1 + 1);
  }

  function furnishHuddleRoom(zone) {
    const midX = Math.floor((zone.x1 + zone.x2) / 2);
    const midY = Math.floor((zone.y1 + zone.y2) / 2);
    buildPrivateRoom(zone, {
      doors: [{ gx: midX, gy: zone.y2 }],
    });
    F("table", midX - 1, midY, 2, 1);
    block(midX - 1, midY);
    block(midX, midY);
    F("chair", midX - 1, midY - 1, 1, 1, "S");
    seat(midX - 1, midY - 1, "S");
    F("chair", midX, midY - 1, 1, 1, "S");
    seat(midX, midY - 1, "S");
    F("chair", midX - 1, midY + 1, 1, 1, "N");
    seat(midX - 1, midY + 1, "N");
    F("chair", midX, midY + 1, 1, 1, "N");
    seat(midX, midY + 1, "N");
  }

  /** Rebuild walk grid, zones, and static furniture from a layout document. */
  function rebuildStaticMap(layout) {
    currentLayout = cloneMapLayout(layout || DEFAULT_MAP_LAYOUT);
    officeSlots = currentLayout.offices;
    commonZones = currentLayout.commonZones;

    // Clear walk + seats + static furniture
    walk.fill(1);
    for (const k of Object.keys(seats)) delete seats[k];
    furn.length = 0;
    ZONES.length = 0;

    for (const z of commonZones) {
      ZONES.push({ ...z });
    }
    for (const s of officeSlots) {
      ZONES.push({
        id: s.slotKey,
        name: s.nameDefault,
        x1: s.x1,
        y1: s.y1,
        x2: s.x2,
        y2: s.y2,
        rug: "#E8F2FB",
        priv: true,
        personal: true,
      });
    }

    // Outer shell
    for (let x = 0; x < COLS; x++) {
      block(x, 0);
      block(x, ROWS - 1);
    }
    for (let y = 0; y < ROWS; y++) {
      block(0, y);
      block(COLS - 1, y);
    }

    /**
     * Wide doorway openings (walkable): primary door + one neighbor along the wall.
     * Only the primary tile gets a door plaque; the neighbor is an open gap.
     */
    function officeDoorOpenings(s) {
      const tiles = [{ gx: s.door.gx, gy: s.door.gy }];
      const spanX = s.x2 - s.x1;
      const spanY = s.y2 - s.y1;
      if (s.doorWall === "S" || s.doorWall === "N") {
        // Prefer opening toward +x, keep both ends as walls when possible
        if (spanX >= 4 && s.door.gx + 1 < s.x2) {
          tiles.push({ gx: s.door.gx + 1, gy: s.door.gy });
        } else if (spanX >= 4 && s.door.gx - 1 > s.x1) {
          tiles.push({ gx: s.door.gx - 1, gy: s.door.gy });
        }
      } else if (spanY >= 4 && s.door.gy + 1 < s.y2) {
        tiles.push({ gx: s.door.gx, gy: s.door.gy + 1 });
      } else if (spanY >= 4 && s.door.gy - 1 > s.y1) {
        tiles.push({ gx: s.door.gx, gy: s.door.gy - 1 });
      }
      return tiles;
    }

    function isDoorOpening(openings, gx, gy) {
      return openings.some((d) => d.gx === gx && d.gy === gy);
    }

    function isDoorLabel(s, gx, gy) {
      return s.door.gx === gx && s.door.gy === gy;
    }

    // Adjacent pods share partition tiles — place wall/door furniture at most once per cell.
    const wallCellClaimed = new Set();
    function claimWallCell(gx, gy) {
      const key = `${gx},${gy}`;
      if (wallCellClaimed.has(key)) return false;
      wallCellClaimed.add(key);
      return true;
    }

    // Personal enclosed offices — solid back wall, glass door-wall + sides, wide door
    officeSlots.forEach((s) => {
      for (let x = s.x1; x <= s.x2; x++) {
        block(x, s.y1);
        block(x, s.y2);
      }
      for (let y = s.y1; y <= s.y2; y++) {
        block(s.x1, y);
        block(s.x2, y);
      }
      const openings = officeDoorOpenings(s);
      for (const d of openings) {
        walk[d.gy * COLS + d.gx] = 1;
      }
      const seatDir = s.seatDir || "S";
      const deskDir = s.deskDir || "N";
      F("chair", s.interiorSeat.gx, s.interiorSeat.gy, 1, 1, seatDir);
      seat(s.interiorSeat.gx, s.interiorSeat.gy, seatDir);
      F("desk", s.desk.gx, s.desk.gy, 1, 1, deskDir);
      block(s.desk.gx, s.desk.gy);

      // Horizontal edges (N / S)
      for (let x = s.x1; x <= s.x2; x++) {
        // North edge
        if (isDoorOpening(openings, x, s.y1)) {
          if (isDoorLabel(s, x, s.y1) && claimWallCell(x, s.y1)) {
            F("door", x, s.y1, 1, 1, "S", { slotKey: s.slotKey });
          } else {
            // Secondary opening tile — walkable gap, no second plaque
            claimWallCell(x, s.y1);
          }
        } else if (claimWallCell(x, s.y1)) {
          if (s.doorWall === "N") {
            // Door facade — glass (frosts when closed at draw time)
            F("gwall", x, s.y1, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          } else if (s.doorWall === "S") {
            // Back wall opposite the door
            F("wall", x, s.y1, 1, 1, "S");
          } else {
            // Side edge for E/W-door pods
            F("gwall", x, s.y1, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          }
        }
        // South edge
        if (isDoorOpening(openings, x, s.y2)) {
          if (isDoorLabel(s, x, s.y2) && claimWallCell(x, s.y2)) {
            F("door", x, s.y2, 1, 1, "S", { slotKey: s.slotKey });
          } else {
            claimWallCell(x, s.y2);
          }
        } else if (claimWallCell(x, s.y2)) {
          if (s.doorWall === "S") {
            F("gwall", x, s.y2, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          } else if (s.doorWall === "N") {
            F("wall", x, s.y2, 1, 1, "S");
          } else {
            F("gwall", x, s.y2, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          }
        }
      }
      // Vertical edges (E / W), skip corners already drawn
      for (let y = s.y1 + 1; y < s.y2; y++) {
        if (isDoorOpening(openings, s.x2, y)) {
          if (isDoorLabel(s, s.x2, y) && claimWallCell(s.x2, y)) {
            F("door", s.x2, y, 1, 1, "S", { slotKey: s.slotKey });
          } else {
            claimWallCell(s.x2, y);
          }
        } else if (claimWallCell(s.x2, y)) {
          if (s.doorWall === "E") {
            F("gwallW", s.x2, y, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          } else if (s.doorWall === "W") {
            F("wall", s.x2, y, 1, 1, "S");
          } else {
            F("gwallW", s.x2, y, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          }
        }
        if (isDoorOpening(openings, s.x1, y)) {
          if (isDoorLabel(s, s.x1, y) && claimWallCell(s.x1, y)) {
            F("door", s.x1, y, 1, 1, "S", { slotKey: s.slotKey });
          } else {
            claimWallCell(s.x1, y);
          }
        } else if (claimWallCell(s.x1, y)) {
          if (s.doorWall === "W") {
            F("gwallW", s.x1, y, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          } else if (s.doorWall === "E") {
            F("wall", s.x1, y, 1, 1, "S");
          } else {
            F("gwallW", s.x1, y, 1, 1, "S", { slotKey: s.slotKey, facade: true });
          }
        }
      }
    });

    for (const zone of commonZones) {
      if (zone.roomKind === "meeting") furnishMeetingRoom(zone);
      else if (zone.roomKind === "huddle") furnishHuddleRoom(zone);
    }

    // Free furniture from layout document.
    // When seat/block are omitted (stale published rows, hand edits), fall back to
    // catalog KIND_META via furniturePlacementDefaults so Habbo seating/blockers
    // stay interactable after art upgrades.
    for (const item of currentLayout.furniture) {
      // Pass through optional draw props (campus building color / entrance labels / style).
      const extras = {};
      if (item.c) extras.c = item.c;
      if (item.displayName) extras.displayName = item.displayName;
      if (item.slotKey) extras.slotKey = item.slotKey;
      if (item.facade) extras.facade = item.facade;
      if (item.doorWall) extras.doorWall = item.doorWall;
      if (item.style) extras.style = item.style;
      // Tall outdoor facades sort after neighbors so billboards don't sink under trees/lamps
      if (item.kind === "building") extras.depthBias = 120;
      F(item.kind, item.gx, item.gy, item.w || 1, item.d || 1, item.dir || "S", extras);
      const defaults = furniturePlacementDefaults(item.kind);
      const isSeat = item.seat ?? defaults.seat ?? false;
      const isBlock = item.block ?? defaults.block ?? false;
      if (isSeat) seat(item.gx, item.gy, item.dir || "S");
      if (isBlock) {
        // Buildings: full mass footprint is solid (sprite is tight-cropped to match).
        // Door pieces force-walk the entrance tile after this loop.
        for (let dx = 0; dx < (item.w || 1); dx++) {
          for (let dy = 0; dy < (item.d || 1); dy++) {
            block(item.gx + dx, item.gy + dy);
          }
        }
      }
      // Campus building doors stay walkable even if a facade neighbor blocked them.
      if (item.kind === "door") {
        walk[item.gy * COLS + item.gx] = 1;
      }
    }
  }

  rebuildStaticMap(DEFAULT_MAP_LAYOUT);

  // ---- pathfinding (BFS 4-dir) ----
  function findPath(sx, sy, tx, ty) {
    if (sx === tx && sy === ty) return [];
    const tSeat = !isWalk(tx, ty) && !seats[tx + "," + ty] ? null : true;
    const ok = (x, y) => isWalk(x, y) || (x === tx && y === ty && seats[x + "," + y]);
    if (!ok(tx, ty)) return null;
    const prev = new Int32Array(COLS * ROWS).fill(-1);
    const q = [sy * COLS + sx];
    prev[sy * COLS + sx] = sy * COLS + sx;
    while (q.length) {
      const cur = q.shift(),
        cx = cur % COLS,
        cy = (cur / COLS) | 0;
      if (cx === tx && cy === ty) {
        const path = [];
        let c = cur;
        while (c !== sy * COLS + sx) {
          path.unshift({ gx: c % COLS, gy: (c / COLS) | 0 });
          c = prev[c];
        }
        return path;
      }
      [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ].forEach(([dx, dy]) => {
        const nx = cx + dx,
          ny = cy + dy;
        if (ok(nx, ny) && prev[ny * COLS + nx] === -1) {
          prev[ny * COLS + nx] = cur;
          q.push(ny * COLS + nx);
        }
      });
    }
    return null;
  }

  // ---- avatar painting ----
  const OUTLINE = "#101728";

  function normalizeColors(c) {
    c = c || {};
    let style = c.style || "short";
    let hat = c.hat || "none";
    if (style === "cap") {
      hat = hat === "none" ? "cap" : hat;
      style = "short";
    }
    const topStyle = c.topStyle || "solid";
    const bottomStyle = c.bottomStyle || "solid";
    const shoeStyle = c.shoeStyle || "sneakers";
    const expression = c.expression || "neutral";
    const backpack = c.backpack || "none";
    const okTop =
      topStyle === "tee" ||
      topStyle === "polo" ||
      topStyle === "hoodie" ||
      topStyle === "stripes" ||
      topStyle === "blazer" ||
      topStyle === "vest" ||
      topStyle === "sweater" ||
      topStyle === "flannel" ||
      topStyle === "tank"
        ? topStyle
        : "solid";
    const okBottom =
      bottomStyle === "jeans" ||
      bottomStyle === "shorts" ||
      bottomStyle === "skirt" ||
      bottomStyle === "cargo"
        ? bottomStyle
        : "solid";
    const okShoe =
      shoeStyle === "boots" || shoeStyle === "loafers" || shoeStyle === "sandals"
        ? shoeStyle
        : "sneakers";
    const okExpr =
      expression === "smile" || expression === "grin" || expression === "serious"
        ? expression
        : "neutral";
    const okBag =
      backpack === "tote" || backpack === "messenger" || backpack === "laptop-bag"
        ? backpack
        : "none";
    return {
      shirt: c.shirt || "#1E6CC7",
      hair: c.hair || "#2B2118",
      skin: c.skin || "#E9BE93",
      style,
      pants: c.pants || "#1F2A44",
      shoes: c.shoes || "#001847",
      hat,
      hatColor: c.hatColor || c.shirt || "#1E6CC7",
      glasses: c.glasses || "none",
      facialHair: c.facialHair || "none",
      jewelry: c.jewelry || "none",
      topStyle: okTop,
      bottomStyle: okBottom,
      shoeStyle: okShoe,
      eyeColor: c.eyeColor || "#101728",
      expression: okExpr,
      backpack: okBag,
    };
  }

  /**
   * Habbo-style procedural pixel avatar.
   * Layer order: backpack(back) → shoes → legs → body → arms → neck → head →
   * face details → hair → hat → backpack(side).
   * Optional o.action = { id, t } where t is seconds into the action.
   * Arm poses + hop are driven by action id; walk `frame` still drives leg swap.
   */
  function drawAvatar(ctx, cx, fy, facing, frame, colorsIn, o) {
    o = o || {};
    const colors = normalizeColors(colorsIn);
    const u = o.u || 2,
      sit = o.sitting;
    const front = facing === "SE" || facing === "SW";
    const m = facing === "SW" || facing === "NW" ? -1 : 1;
    const act = o.action;
    const at = act ? act.t : 0;
    const aid = act ? act.id : null;
    // Continuous walk phase (0..1) from the caller's step timer → smooth limb swing
    // instead of a stiff 2-frame toggle. Idle/standing (no walk phase) is unchanged.
    const walkPh = typeof o.walk === "number" ? o.walk : null;
    const moving = walkPh != null && !sit && !aid;
    const swing = moving ? Math.sin(walkPh * Math.PI * 2) : 0;
    const walking = (moving || frame === 1) && !sit && !aid;

    let hop = 0;
    let sway = 0;
    let armPose = "idle";
    if (aid === "jump" && !sit) {
      hop = Math.sin(Math.min(1, at / 0.55) * Math.PI) * 5;
    } else if (aid === "dance") {
      hop = Math.abs(Math.sin(at * 10)) * 1.6;
      sway = Math.sin(at * 12) * 1.4;
      armPose = "dance";
    } else if (aid === "wave") {
      armPose = "wave";
    } else if (aid === "handshake") {
      armPose = "handshake";
    } else if (aid === "clap") {
      armPose = "clap";
    } else if (aid === "cheer") {
      hop = Math.abs(Math.sin(at * 8)) * 1.2;
      armPose = "cheer";
    }

    /** Filled rect in avatar local units. */
    const r = (x, y, w, h, c) => {
      if (w <= 0 || h <= 0) return;
      ctx.fillStyle = c;
      ctx.fillRect(
        Math.round(cx + (x + sway) * u),
        Math.round(fy + (y - hop) * u),
        Math.max(1, Math.round(w * u)),
        Math.max(1, Math.round(h * u)),
      );
    };
    /** Dark outline then fill — Habbo silhouette cue. Outline scales with u. */
    const ro = (x, y, w, h, c) => {
      r(x - 0.5, y - 0.5, w + 1, h + 1, OUTLINE);
      r(x, y, w, h, c);
    };
    /** Body block with right/bottom shade edge. */
    const rShade = (x, y, w, h, c) => {
      ro(x, y, w, h, c);
      if (w > 1 && h > 1) {
        r(x + w - 1, y + 1, 1, h - 1, shade(c, -28));
        r(x + 1, y + h - 1, w - 1, 1, shade(c, -22));
      }
    };

    const pant = colors.pants,
      shoe = colors.shoes,
      shirt = colors.shirt;
    const top = colors.topStyle;
    const bottom = colors.bottomStyle;
    const shoeSt = colors.shoeStyle;
    const bob = walking ? -1 : 0;
    const sleeveW =
      top === "tank" ? 1.5 : top === "tee" || top === "polo" || top === "vest" ? 2 : 2.5;
    const armY = sit ? -10 : -16;
    // Arms swing opposite each other; continuous while moving, else the old 2-pose offset.
    const walkArm = moving ? swing * 1.7 : walking ? 1.2 : 0;

    const drawShoes = (lx, rx, ly, ry, shH) => {
      if (shoeSt === "boots") {
        ro(lx, ly - 1, 4, shH + 2, shoe);
        ro(rx, ry - 1, 4, shH + 2, shoe);
        r(lx + 1, ly - 1, 2, 1, shade(shoe, 30));
        r(rx + 1, ry - 1, 2, 1, shade(shoe, 30));
      } else if (shoeSt === "loafers") {
        ro(lx, ly, 4, shH, shoe);
        ro(rx, ry, 4, shH, shoe);
        r(lx + 3, ly, 1, shH, shade(shoe, -25));
        r(rx + 3, ry, 1, shH, shade(shoe, -25));
      } else if (shoeSt === "sandals") {
        ro(lx, ly + 1, 4, Math.max(1, shH - 1), shoe);
        ro(rx, ry + 1, 4, Math.max(1, shH - 1), shoe);
        r(lx, ly, 4, 1, colors.skin);
        r(rx, ry, 4, 1, colors.skin);
      } else {
        // sneakers — white stripe
        ro(lx, ly, 4, shH, shoe);
        ro(rx, ry, 4, shH, shoe);
        r(lx, ly, 4, 1, shade(shoe, 40));
        r(rx, ry, 4, 1, shade(shoe, 40));
      }
    };

    const drawLegsStanding = (l1, l2, tuck) => {
      const legH1 = Math.max(2, l1 - tuck);
      const legH2 = Math.max(2, l2 - tuck);
      if (bottom === "skirt") {
        rShade(-6, -2 - Math.max(legH1, legH2) + 2, 12, Math.max(legH1, legH2) - 1, pant);
        r(-5, -2 - Math.max(legH1, legH2) + 3, 10, 1, shade(pant, 25));
        // shin peek
        r(-4, -2 - 2, 3, 2, colors.skin);
        r(1, -2 - 2, 3, 2, colors.skin);
      } else if (bottom === "shorts") {
        const sh = Math.max(2, Math.floor(Math.min(legH1, legH2) * 0.55));
        rShade(-5, -2 - legH1, 4, sh, pant);
        rShade(1, -2 - legH2, 4, sh, pant);
        r(-5, -2 - legH1 + sh, 4, legH1 - sh, colors.skin);
        r(1, -2 - legH2 + sh, 4, legH2 - sh, colors.skin);
      } else {
        rShade(-5, -2 - legH1, 4, legH1, pant);
        rShade(1, -2 - legH2, 4, legH2, pant);
        if (bottom === "jeans") {
          r(-4, -2 - legH1 + 2, 1, legH1 - 3, shade(pant, 35));
          r(2, -2 - legH2 + 2, 1, legH2 - 3, shade(pant, 35));
        } else if (bottom === "cargo") {
          r(-6, -2 - Math.floor(legH1 * 0.45), 2, 3, shade(pant, -20));
          r(4, -2 - Math.floor(legH2 * 0.45), 2, 3, shade(pant, -20));
        }
      }
      drawShoes(-5, 1, -2, -2, 2);
    };

    const drawBackpack = (yBob, mode) => {
      const bag = colors.backpack;
      if (bag === "none") return;
      const bagC = bag === "tote" ? "#5C4033" : bag === "messenger" ? "#1F2A44" : "#2C3E50";
      const strap = shade(bagC, 40);
      if (mode === "back" && !front) {
        if (bag === "tote") {
          ro(-5, -18 + yBob, 10, 8, bagC);
          r(-4, -17 + yBob, 8, 2, strap);
          r(-3, -16 + yBob, 2, 5, shade(bagC, -30));
        } else if (bag === "messenger") {
          ro(-6, -16 + yBob, 12, 6, bagC);
          r(-5, -15 + yBob, 10, 1, strap);
          r(m > 0 ? 4 : -6, -20 + yBob, 2, 6, strap);
        } else {
          // laptop-bag
          ro(-6, -17 + yBob, 12, 7, bagC);
          r(-5, -16 + yBob, 10, 1, "#2D9BD6");
          r(-4, -14 + yBob, 8, 3, shade(bagC, 20));
        }
      } else if (mode === "side" && front) {
        const sx = m > 0 ? 6 : -9;
        if (bag === "tote") {
          ro(sx, -14 + yBob, 3, 6, bagC);
          r(sx, -15 + yBob, 3, 1, strap);
        } else if (bag === "messenger") {
          ro(sx, -13 + yBob, 4, 5, bagC);
          r(sx - (m > 0 ? 1 : 0), -16 + yBob, 2, 4, strap);
        } else {
          ro(sx, -14 + yBob, 4, 6, bagC);
          r(sx + 1, -13 + yBob, 2, 1, "#2D9BD6");
        }
      }
    };

    const drawTopDetails = (tx, ty, tw, th) => {
      if (top === "stripes") {
        r(tx + 1, ty + 2, tw - 2, 1, shade(shirt, 40));
        r(tx + 1, ty + 5, tw - 2, 1, shade(shirt, 40));
      } else if (top === "polo") {
        r(tx + tw / 2 - 1, ty, 2, 2, shade(shirt, -35));
        r(tx + tw / 2 - 1.5, ty, 1, 1, shade(shirt, 25));
        r(tx + tw / 2 + 0.5, ty, 1, 1, shade(shirt, 25));
      } else if (top === "blazer") {
        r(tx + 1, ty + 1, 1, th - 2, shade(shirt, 35));
        r(tx + tw - 2, ty + 1, 1, th - 2, shade(shirt, -40));
        r(tx + tw / 2 - 0.5, ty + 1, 1, th - 2, shade(shirt, -45));
      } else if (top === "tee") {
        r(tx + 1, ty + th - 1, tw - 2, 1, shade(shirt, -30));
      } else if (top === "vest") {
        r(tx + 1, ty + 1, 1, th - 2, shade(shirt, -40));
        r(tx + tw - 2, ty + 1, 1, th - 2, shade(shirt, -40));
        r(tx + tw / 2 - 0.5, ty + 2, 1, th - 3, shade(shirt, 25));
      } else if (top === "sweater") {
        r(tx + 1, ty + 1, tw - 2, 2, shade(shirt, 30));
        r(tx + tw / 2 - 1, ty, 2, 2, shade(shirt, -20));
      } else if (top === "flannel") {
        r(tx + 2, ty + 1, 1, th - 2, shade(shirt, 40));
        r(tx + tw - 3, ty + 1, 1, th - 2, shade(shirt, 40));
        r(tx + 1, ty + 3, tw - 2, 1, shade(shirt, -30));
        r(tx + 1, ty + 6, tw - 2, 1, shade(shirt, -30));
      } else if (top === "tank") {
        r(tx + 2, ty, 1, 2, colors.skin);
        r(tx + tw - 3, ty, 1, 2, colors.skin);
      }
    };

    const drawDefaultArms = (yBob) => {
      const ay = armY + yBob;
      const leftY = ay + walkArm;
      const rightY = ay - walkArm;
      ro(-7, leftY, sleeveW, 7, shirt);
      ro(5, rightY, sleeveW, 7, shirt);
      r(-7, leftY + 6, 2, 2, colors.skin);
      r(5, rightY + 6, 2, 2, colors.skin);
      if (colors.jewelry === "watch") {
        r(-7, leftY + 5, 2, 1, "#E8A93C");
      }
    };

    const drawActionArms = (yBob) => {
      if (armPose === "wave") {
        const wb = Math.sin(at * 14) * 2;
        ro(-7, armY + yBob, 2, 7, shirt);
        r(-7, armY + yBob + 6, 2, 2, colors.skin);
        ro(5, -24 + yBob + wb, 2, 10, shirt);
        r(5, -26 + yBob + wb, 2, 2, colors.skin);
        return;
      }
      if (armPose === "dance") {
        const a1 = Math.sin(at * 14) * 3;
        const a2 = Math.sin(at * 14 + Math.PI) * 3;
        ro(-8, -19 + yBob + a1, 2, 8, shirt);
        ro(6, -19 + yBob + a2, 2, 8, shirt);
        r(-8, -21 + yBob + a1, 2, 2, colors.skin);
        r(6, -21 + yBob + a2, 2, 2, colors.skin);
        return;
      }
      if (armPose === "handshake") {
        ro(-7, armY + yBob, 2, 7, shirt);
        r(-7, armY + yBob + 6, 2, 2, colors.skin);
        ro(5, -14 + yBob, 7, 2, shirt);
        r(11, -14 + yBob, 2, 2, colors.skin);
        return;
      }
      if (armPose === "clap") {
        const open = Math.sin(at * 18) > 0;
        if (open) {
          ro(-4, -15 + yBob, 2, 6, shirt);
          ro(2, -15 + yBob, 2, 6, shirt);
          r(-4, -10 + yBob, 2, 2, colors.skin);
          r(2, -10 + yBob, 2, 2, colors.skin);
        } else {
          ro(-2, -15 + yBob, 2, 6, shirt);
          ro(0, -15 + yBob, 2, 6, shirt);
          r(-1, -10 + yBob, 2, 2, colors.skin);
        }
        return;
      }
      if (armPose === "cheer") {
        const cb = Math.sin(at * 10) * 1.2;
        ro(-8, -25 + yBob + cb, 2, 10, shirt);
        ro(6, -25 + yBob - cb, 2, 10, shirt);
        r(-8, -27 + yBob + cb, 2, 2, colors.skin);
        r(6, -27 + yBob - cb, 2, 2, colors.skin);
        return;
      }
      drawDefaultArms(yBob);
    };

    const drawNeck = (yBob) => {
      const j = colors.jewelry;
      if (j === "tie") {
        ro(-1, -11 + yBob, 2, 7, "#D8443C");
        r(-1, -11 + yBob, 2, 1, "#001847");
      } else if (j === "scarf") {
        ro(-6, -17 + yBob, 12, 2, "#2D9BD6");
        r(4, -15 + yBob, 2, 5, "#2D9BD6");
      } else if (j === "lanyard") {
        r(-1, -17 + yBob, 2, 1, "#E8A93C");
        r(-1, -16 + yBob, 2, 6, "#E8A93C");
        ro(-2, -11 + yBob, 4, 2, "#001847");
      } else if (j === "necklace") {
        r(-3, -17 + yBob, 6, 1, "#C9A227");
        r(-1, -16 + yBob, 2, 1, "#C9A227");
      }
    };

    // Hood bulk drawn behind head for hoodie tops
    const drawHoodBack = (hy) => {
      if (top !== "hoodie" || colors.hat !== "none") return;
      ro(-6, hy - 3, 12, 5, shade(shirt, -15));
      r(-5, hy - 2, 10, 3, shade(shirt, 10));
    };

    if (sit) {
      fy -= 7 * u;
      drawBackpack(0, "back");
      if (bottom === "skirt") {
        rShade(-6, -4, 12, 5, pant);
      } else {
        rShade(-6, -2, 12, 3, pant);
      }
      drawShoes(-6 + m, 2 * m - (m < 0 ? 4 : 0), -5, -5, 3);
      rShade(-6, -12, 12, 10, shirt);
      drawTopDetails(-6, -12, 12, 10);
      if (armPose === "idle") {
        ro(-8, -11, 2, 6, shirt);
        ro(6, -11, 2, 6, shirt);
        r(-8, -6, 2, 2, colors.skin);
        r(6, -6, 2, 2, colors.skin);
      } else {
        drawActionArms(6);
      }
      drawNeck(0);
      drawBackpack(0, "side");
    } else {
      // Legs lift in opposition — continuous stride while moving, else 2-pose fallback.
      const l1 = moving ? 5.5 - swing * 1.1 : frame === 1 || aid === "dance" ? 5 : 6,
        l2 = moving ? 5.5 + swing * 1.1 : frame === 1 || aid === "dance" ? 6 : 5;
      const tuck = aid === "jump" ? Math.sin(Math.min(1, at / 0.55) * Math.PI) * 2 : 0;
      drawBackpack(bob, "back");
      drawLegsStanding(l1, l2, tuck);
      rShade(-6, -17 + bob, 12, 9, shirt);
      drawTopDetails(-6, -17 + bob, 12, 9);
      drawActionArms(bob);
      drawNeck(bob);
      drawBackpack(bob, "side");
    }

    // Head — chunkier Habbo proportions (9×9 face)
    const hy = (sit ? -20 : -27) + bob;
    drawHoodBack(hy);
    rShade(-5, hy, 10, 9, colors.skin);
    // forehead highlight
    r(-3, hy + 1, 4, 1, shade(colors.skin, 22));

    // Face
    if (front) {
      const eye = colors.eyeColor || "#101728";
      // Eye whites + pupils
      r(-3 + m, hy + 3, 2, 2, "#FFFFFF");
      r(2 + m, hy + 3, 2, 2, "#FFFFFF");
      r(-2 + m, hy + 3, 1, 2, eye);
      r(3 + m, hy + 3, 1, 2, eye);
      // Eyebrows — a little brow definition so the face reads less blank up close.
      const browC = shade(colors.hair, -10);
      r(-3 + m, hy + 2, 2, 1, browC);
      r(2 + m, hy + 2, 2, 1, browC);
      // Mouth by expression
      const expr = colors.expression;
      if (expr === "smile") {
        r(m - 1, hy + 6, 3, 1, shade(colors.skin, -45));
        r(m - 1, hy + 6, 1, 1, shade(colors.skin, -20));
        r(m + 1, hy + 6, 1, 1, shade(colors.skin, -20));
      } else if (expr === "grin") {
        r(m - 2, hy + 6, 4, 1, "#FFFFFF");
        r(m - 2, hy + 5, 1, 1, shade(colors.skin, -40));
        r(m + 1, hy + 5, 1, 1, shade(colors.skin, -40));
      } else if (expr === "serious") {
        r(m - 1, hy + 6, 2, 1, shade(colors.skin, -50));
        r(m - 2, hy + 2, 2, 1, shade(colors.skin, -25));
        r(m + 1, hy + 2, 2, 1, shade(colors.skin, -25));
      } else {
        r(m - 1, hy + 6, 2, 1, shade(colors.skin, -40));
      }
      // Facial hair
      if (colors.facialHair === "stubble") {
        r(-3, hy + 5, 6, 2, shade(colors.hair, 45));
      } else if (colors.facialHair === "mustache") {
        r(-2, hy + 5, 4, 1, colors.hair);
      } else if (colors.facialHair === "beard") {
        r(-4, hy + 5, 8, 3, colors.hair);
        r(-3, hy + 7, 6, 1, colors.hair);
      } else if (colors.facialHair === "goatee") {
        r(-1, hy + 5, 2, 1, colors.hair);
        r(-2, hy + 6, 4, 2, colors.hair);
      }
      // Glasses
      if (colors.glasses === "glasses") {
        ro(-4, hy + 2, 3, 3, "#101728");
        ro(1, hy + 2, 3, 3, "#101728");
        r(-1, hy + 3, 2, 1, "#101728");
        r(-3, hy + 3, 1, 1, "rgba(200,220,255,0.55)");
        r(2, hy + 3, 1, 1, "rgba(200,220,255,0.55)");
      } else if (colors.glasses === "round") {
        ro(-4, hy + 2, 3, 3, "#2B2118");
        ro(1, hy + 2, 3, 3, "#2B2118");
        r(-1, hy + 3, 2, 1, "#2B2118");
      } else if (colors.glasses === "shades") {
        ro(-5, hy + 2, 10, 3, "#101728");
        r(-4, hy + 3, 8, 1, shade("#101728", 40));
      } else if (colors.glasses === "half") {
        r(-4, hy + 3, 3, 1, "#101728");
        r(1, hy + 3, 3, 1, "#101728");
        r(-1, hy + 3, 2, 1, "#101728");
        r(-4, hy + 2, 3, 1, "#8894A8");
        r(1, hy + 2, 3, 1, "#8894A8");
      } else if (colors.glasses === "monocle") {
        ro(1, hy + 2, 3, 3, "#C9A227");
        r(2, hy + 3, 1, 1, "rgba(200,220,255,0.55)");
        r(4, hy + 5, 1, 2, "#C9A227");
      } else if (colors.glasses === "sports") {
        ro(-5, hy + 2, 10, 3, "#1E6CC7");
        r(-4, hy + 3, 8, 1, shade("#1E6CC7", 40));
        r(-5, hy + 1, 10, 1, "#101728");
      } else if (colors.glasses === "star") {
        ro(-5, hy + 2, 10, 3, "#9B5DE5");
        r(-3, hy + 2, 1, 1, "#FFD65A");
        r(2, hy + 2, 1, 1, "#FFD65A");
      }
    } else {
      // Back of head ear hint
      r(-5, hy + 3, 1, 2, shade(colors.skin, -20));
      r(4, hy + 3, 1, 2, shade(colors.skin, -20));
    }

    // Hair
    const hc = colors.hair;
    const hs = colors.style;
    const hairHi = shade(hc, 35);
    if (hs === "long") {
      rShade(-6, hy - 2, 12, 3, hc);
      r(-5, hy - 2, 6, 1, hairHi);
      r(-6, hy, 2, 10, hc);
      r(4, hy, 2, 10, hc);
      if (!front) rShade(-5, hy, 10, 6, hc);
    } else if (hs === "bun") {
      rShade(-5, hy - 2, 10, 3, hc);
      r(-4, hy - 2, 5, 1, hairHi);
      ro(-3, hy - 6, 6, 4, hc);
      r(-2, hy - 5, 3, 1, hairHi);
      if (!front) rShade(-5, hy, 10, 3, hc);
    } else if (hs === "afro") {
      rShade(-7, hy - 5, 14, 7, hc);
      r(-5, hy - 4, 6, 2, hairHi);
      r(-6, hy + 1, 2, 4, hc);
      r(4, hy + 1, 2, 4, hc);
      if (!front) rShade(-6, hy, 12, 5, hc);
    } else if (hs === "fade") {
      rShade(-4, hy - 1, 8, 2, hc);
      r(-3, hy - 1, 4, 1, hairHi);
      r(-5, hy + 1, 1, 2, shade(hc, 50));
      r(4, hy + 1, 1, 2, shade(hc, 50));
      if (!front) rShade(-4, hy, 8, 2, hc);
    } else if (hs === "ponytail") {
      rShade(-5, hy - 2, 10, 3, hc);
      r(-4, hy - 2, 5, 1, hairHi);
      if (front) {
        ro(m > 0 ? 4 : -6, hy + 2, 2, 7, hc);
      } else {
        ro(-1, hy + 2, 2, 8, hc);
        rShade(-5, hy, 10, 3, hc);
      }
    } else if (hs === "side-part") {
      rShade(-5, hy - 2, 10, 3, hc);
      r(m > 0 ? -5 : 2, hy - 2, 4, 1, hairHi);
      r(m > 0 ? -5 : 4, hy + 1, 1, 3, hc);
      r(m > 0 ? 3 : -5, hy - 1, 2, 2, hc);
      if (!front) rShade(-5, hy, 10, 4, hc);
    } else if (hs === "curly") {
      rShade(-6, hy - 3, 12, 4, hc);
      r(-5, hy - 2, 4, 1, hairHi);
      r(-6, hy + 1, 2, 3, hc);
      r(-3, hy + 1, 2, 2, hc);
      r(1, hy + 1, 2, 2, hc);
      r(4, hy + 1, 2, 3, hc);
      if (!front) rShade(-5, hy, 10, 4, hc);
    } else if (hs === "mohawk") {
      ro(-1, hy - 6, 2, 7, hc);
      r(-1, hy - 5, 2, 1, hairHi);
      r(-2, hy - 3, 4, 2, hc);
      if (!front) r(-1, hy, 2, 3, hc);
    } else if (hs === "spiky") {
      rShade(-5, hy - 2, 10, 3, hc);
      r(-4, hy - 5, 2, 3, hc);
      r(-1, hy - 6, 2, 4, hc);
      r(2, hy - 5, 2, 3, hc);
      r(-1, hy - 5, 2, 1, hairHi);
      if (!front) rShade(-5, hy, 10, 3, hc);
    } else if (hs === "braids") {
      rShade(-5, hy - 2, 10, 3, hc);
      r(-4, hy - 2, 5, 1, hairHi);
      r(-6, hy + 1, 2, 8, hc);
      r(4, hy + 1, 2, 8, hc);
      r(-6, hy + 8, 2, 1, hairHi);
      r(4, hy + 8, 2, 1, hairHi);
      if (!front) rShade(-5, hy, 10, 4, hc);
    } else if (hs === "locs") {
      rShade(-6, hy - 3, 12, 4, hc);
      r(-5, hy - 2, 4, 1, hairHi);
      for (let i = 0; i < 5; i++) {
        r(-6 + i * 2, hy + 1, 2, 7 + (i % 2), hc);
      }
      if (!front) rShade(-5, hy, 10, 5, hc);
    } else if (hs === "buzz") {
      rShade(-4, hy - 1, 8, 2, shade(hc, 20));
      if (!front) rShade(-4, hy, 8, 2, shade(hc, 10));
    } else if (hs === "wavy") {
      rShade(-6, hy - 3, 12, 4, hc);
      r(-5, hy - 2, 5, 1, hairHi);
      r(-6, hy + 1, 2, 5, hc);
      r(-3, hy + 2, 2, 4, hc);
      r(1, hy + 1, 2, 5, hc);
      r(4, hy + 2, 2, 4, hc);
      if (!front) rShade(-5, hy, 10, 4, hc);
    } else if (hs !== "none") {
      rShade(-5, hy - 2, 10, 3, hc);
      r(-4, hy - 2, 5, 1, hairHi);
      r(-5, hy + 1, 1, 2, hc);
      r(4, hy + 1, 1, 2, hc);
      if (!front) rShade(-5, hy, 10, 4, hc);
    }

    if (colors.jewelry === "earrings" && front) {
      ro(-6, hy + 4, 1, 2, "#C9A227");
      ro(5, hy + 4, 1, 2, "#C9A227");
    }
    if (colors.jewelry === "badge" && front) {
      ro(3, -12, 3, 3, "#E8A93C");
      r(4, -11, 1, 1, "#001847");
    }
    if (colors.jewelry === "ring") {
      r(5, armY + 5, 1, 1, "#C9A227");
    }

    // Hat / headwear
    const hat = colors.hat;
    const hatC = colors.hatColor;
    const hatHi = shade(hatC, 30);
    if (hat === "cap") {
      rShade(-5, hy - 3, 10, 4, hatC);
      r(-4, hy - 3, 5, 1, hatHi);
      ro(m > 0 ? 0 : -7, hy + 1, 7, 1, hatC);
      if (!front) rShade(-5, hy, 10, 2, hatC);
    } else if (hat === "beanie") {
      rShade(-6, hy - 4, 12, 5, hatC);
      r(-3, hy - 5, 6, 1, hatHi);
      r(-2, hy - 4, 4, 1, shade(hatC, 40));
      if (!front) rShade(-5, hy, 10, 2, hatC);
    } else if (hat === "headband") {
      ro(-6, hy + 1, 12, 2, hatC);
      r(-5, hy + 1, 6, 1, hatHi);
    } else if (hat === "headphones") {
      ro(-7, hy + 1, 2, 5, "#1F2A44");
      ro(5, hy + 1, 2, 5, "#1F2A44");
      r(-6, hy - 1, 12, 1, "#1F2A44");
      r(-7, hy + 2, 2, 2, hatC);
      r(5, hy + 2, 2, 2, hatC);
    } else if (hat === "hardhat") {
      rShade(-6, hy - 4, 12, 5, "#E8A93C");
      r(-3, hy - 5, 6, 1, shade("#E8A93C", 30));
      ro(-7, hy + 0, 14, 1, "#E8A93C");
      r(-1, hy - 5, 2, 1, "#001847");
    } else if (hat === "beret") {
      rShade(-6, hy - 4, 11, 4, hatC);
      r(-4, hy - 4, 5, 1, hatHi);
      r(m > 0 ? 3 : -7, hy - 2, 3, 2, hatC);
    } else if (hat === "visor") {
      ro(-6, hy + 1, 12, 2, hatC);
      r(-5, hy + 1, 6, 1, hatHi);
      ro(m > 0 ? 0 : -7, hy + 2, 7, 1, shade(hatC, -20));
    } else if (hat === "bandana") {
      rShade(-6, hy - 1, 12, 3, hatC);
      r(-4, hy - 1, 5, 1, hatHi);
      if (front) {
        ro(m > 0 ? 3 : -6, hy + 2, 2, 4, hatC);
      } else {
        ro(-1, hy + 2, 2, 5, hatC);
      }
    } else if (hat === "crown") {
      rShade(-5, hy - 5, 10, 4, "#E8A93C");
      r(-4, hy - 6, 2, 2, "#E8A93C");
      r(-1, hy - 7, 2, 3, "#FFD65A");
      r(2, hy - 6, 2, 2, "#E8A93C");
      r(-3, hy - 4, 1, 1, "#2D9BD6");
      r(1, hy - 4, 1, 1, "#D8443C");
    } else if (hat === "flower") {
      r(-2, hy - 5, 4, 4, "#FF6B9D");
      r(-1, hy - 4, 2, 2, "#E8A93C");
      r(1, hy - 3, 2, 2, "#2EA86A");
    } else if (hat === "cowboy") {
      rShade(-7, hy - 3, 14, 3, hatC);
      r(-4, hy - 5, 8, 3, hatC);
      r(-3, hy - 5, 4, 1, hatHi);
      ro(-8, hy + 0, 16, 1, shade(hatC, -20));
    } else if (hat === "propeller") {
      rShade(-4, hy - 3, 8, 3, hatC);
      r(-6, hy - 5, 12, 1, "#D8443C");
      r(-1, hy - 7, 2, 3, "#8894A8");
    }
  }

  class VyAvatar extends HTMLElement {
    static get observedAttributes() {
      return [
        "shirt",
        "hair",
        "skin",
        "hstyle",
        "pants",
        "shoes",
        "hat",
        "hatcolor",
        "glasses",
        "facialhair",
        "jewelry",
        "topstyle",
        "bottomstyle",
        "shoestyle",
        "eyecolor",
        "expression",
        "backpack",
        "size",
        "spin",
      ];
    }
    connectedCallback() {
      this.cv = document.createElement("canvas");
      this.appendChild(this.cv);
      this.face = 0;
      this.paint();
      this.t = setInterval(() => {
        if (this.getAttribute("spin") !== null) {
          this.face = (this.face + 1) % 4;
          this.paint();
        }
      }, 700);
    }
    disconnectedCallback() {
      clearInterval(this.t);
    }
    attributeChangedCallback() {
      if (this.cv) this.paint();
    }
    paint() {
      const size = parseInt(this.getAttribute("size") || "96", 10);
      // Slightly denser pixels for chunkier Habbo silhouette in hero previews
      const u = Math.max(2, Math.floor(size / 28));
      this.cv.width = size;
      this.cv.height = size;
      this.cv.style.width = size + "px";
      this.cv.style.height = size + "px";
      const ctx = this.cv.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = "rgba(0,24,71,0.10)";
      ctx.beginPath();
      ctx.ellipse(size / 2, size - u * 2, u * 8, u * 2.6, 0, 0, 7);
      ctx.fill();
      drawAvatar(
        ctx,
        size / 2,
        size - u * 2,
        ["SE", "SW", "NW", "NE"][this.face],
        0,
        {
          shirt: this.getAttribute("shirt") || "#1E6CC7",
          hair: this.getAttribute("hair") || "#2B2118",
          skin: this.getAttribute("skin") || "#E9BE93",
          style: this.getAttribute("hstyle") || "short",
          pants: this.getAttribute("pants") || "#1F2A44",
          shoes: this.getAttribute("shoes") || "#001847",
          hat: this.getAttribute("hat") || "none",
          hatColor: this.getAttribute("hatcolor") || "#1E6CC7",
          glasses: this.getAttribute("glasses") || "none",
          facialHair: this.getAttribute("facialhair") || "none",
          jewelry: this.getAttribute("jewelry") || "none",
          topStyle: this.getAttribute("topstyle") || "solid",
          bottomStyle: this.getAttribute("bottomstyle") || "solid",
          shoeStyle: this.getAttribute("shoestyle") || "sneakers",
          eyeColor: this.getAttribute("eyecolor") || "#101728",
          expression: this.getAttribute("expression") || "neutral",
          backpack: this.getAttribute("backpack") || "none",
        },
        { u },
      );
    }
  }
  customElements.define("vyllage-avatar", VyAvatar);

  // ---- iso paint helpers ----
  function poly(ctx, pts, c) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
    ctx.fill();
  }
  function box(ctx, gx, gy, w, d, h, top, opts) {
    opts = opts || {};
    const A = iso(gx, gy),
      B = iso(gx + w, gy),
      C = iso(gx + w, gy + d),
      D = iso(gx, gy + d);
    const l = opts.left || shade(top, -28),
      r = opts.right || shade(top, -14);
    const leftFace = [
      [D.x, D.y],
      [C.x, C.y],
      [C.x, C.y - h],
      [D.x, D.y - h],
    ];
    const rightFace = [
      [C.x, C.y],
      [B.x, B.y],
      [B.x, B.y - h],
      [C.x, C.y - h],
    ];
    const topFace = [
      [A.x, A.y - h],
      [B.x, B.y - h],
      [C.x, C.y - h],
      [D.x, D.y - h],
    ];
    poly(ctx, leftFace, l);
    poly(ctx, rightFace, r);
    poly(ctx, topFace, top);
    // Dark cell-shaded outline — gives procedural props the same crisp edge as the
    // hero sprite art so a plain box no longer reads as unfinished next to them.
    // Opt out (outline:false) for the baked perimeter shell to avoid a busy back wall.
    if (opts.outline !== false) {
      ctx.strokeStyle = opts.outlineColor || "rgba(12,20,40,0.42)";
      ctx.lineWidth = opts.outlineWidth || 1;
      ctx.lineJoin = "round";
      for (const face of [leftFace, rightFace, topFace]) {
        ctx.beginPath();
        ctx.moveTo(face[0][0], face[0][1]);
        for (let i = 1; i < face.length; i++) ctx.lineTo(face[i][0], face[i][1]);
        ctx.closePath();
        ctx.stroke();
      }
    }
    return { A, B, C, D };
  }
  function faceSW(ctx, gx, gyd, h, fn) {
    // decal plane on a [D->C] face; local x: 32px per tile
    const D = iso(gx, gyd);
    ctx.save();
    ctx.translate(D.x, D.y - h);
    ctx.transform(1, 0.5, 0, 1, 0, 0);
    fn(ctx);
    ctx.restore();
  }
  function faceSE(ctx, gx, gyd, h, fn) {
    // decal plane on a [C->B] face (N/S wall pods)
    const C = iso(gx + 1, gyd);
    ctx.save();
    ctx.translate(C.x, C.y - h);
    ctx.transform(-1, 0.5, 0, 1, 0, 0);
    fn(ctx);
    ctx.restore();
  }
  const FOX = [
    "..n.......n..",
    ".nn.......nn.",
    ".nnn.....nnn.",
    ".nnnnnnnnnnn.",
    "..nnnnnnnnn..",
    "..nnnnnnnnn..",
    "...nnnnnnn...",
    "....nnnnn....",
    "...nnnnnnn...",
    "..nnnnnnnnn.w",
    ".nnnnnnnnnn.w",
    ".nnnnnnnnnnww",
    "..nnnnnnnnww.",
  ];

  /**
   * Tier B hybrid path: draw catalog sprite when loaded; else fall through to procedural.
   * Structural kinds (wall/gwall/door/frame) stay procedural.
   * Placement: bottom-center of sprite on iso center of footprint (gx,gy,w,d).
   */
  function tryDrawFurnitureSprite(ctx, f) {
    const kind = f.kind;
    if (
      kind === "wall" ||
      kind === "gwall" ||
      kind === "gwallW" ||
      kind === "door" ||
      kind === "frame" ||
      kind === "platform"
    ) {
      return false;
    }
    const resolved = resolveFurnitureSprite(kind, f.dir || "S", f.style);
    // Multi-tile / procedural_only kinds return null asset — keep box() geometry
    // so iso depth sorting with chairs (boardroom) still makes sense.
    if (!resolved.useSprite || !resolved.asset) return false;
    const fw = f.w > 0 ? f.w : resolved.footprint.w || 1;
    const fd = f.d > 0 ? f.d : resolved.footprint.d || 1;
    // Outdoor multi-tile: buildings use natural scale (not footprint-stretch);
    // truck/flowerbed still fill their footprint. Indoor multi-tile stay procedural.
    const allowMultiTile = kind === "building" || kind === "delivery_truck" || kind === "flowerbed";
    if ((fw > 1 || fd > 1) && !allowMultiTile) return false;
    const img = ensureSprite(resolved.asset.src);
    if (!img) return false;
    // Natural size 0 or failed decode → procedural fallback
    if (!img.naturalWidth || !img.naturalHeight) return false;
    let rect;
    // Hoisted to function-body scope: the foliage underpaint in the try block below
    // reads center.x/center.y. When center was block-scoped to these if/else branches
    // it was undefined in the try, throwing a ReferenceError that the catch swallowed —
    // silently forcing EVERY foliage sprite (tree/pine/bush/hedge/plant/planter) onto
    // its procedural box fallback (the "bright green boxes" on the plaza).
    let center;
    if (kind === "building") {
      // Plant the facade foot on the door-face edge of the shell so the art sits
      // on the plaza approach (not floating mid-mass). Keep natural aspect.
      const wall = f.doorWall || "S";
      let cx = f.gx + fw / 2;
      let cy = f.gy + fd / 2;
      if (wall === "S") cy = f.gy + fd - 0.2;
      else if (wall === "N") cy = f.gy + 0.2;
      else if (wall === "E") cx = f.gx + fw - 0.2;
      else cx = f.gx + 0.2;
      center = iso(cx, cy);
      rect = spriteDrawRect(center.x, center.y, resolved.asset, buildingDrawScale(f.style));
    } else {
      const { cx, cy } = footprintCenter(f.gx, f.gy, fw, fd);
      center = iso(cx, cy);
      // flowerbed is a flat ground bed → fill its footprint. Everything else (incl.
      // delivery_truck) is sized by the shared person-anchored scale authority off the
      // sprite's measured content height, so relative heights stay correct regardless
      // of the stored footprint. Kinds absent from KIND_HEIGHT resolve to scale 1.
      if (kind === "flowerbed") {
        rect = spriteDrawRectForFootprint(center.x, center.y, resolved.asset, fw, fd, TW, 0.92);
      } else {
        const contentH = measureContentHeight(img) || resolved.asset.height;
        rect = spriteDrawRect(center.x, center.y, resolved.asset, spriteScaleFor(kind, contentH));
      }
    }
    try {
      const isFoliage =
        kind === "tree" ||
        kind === "pine" ||
        kind === "bush" ||
        kind === "hedge" ||
        kind === "plant" ||
        kind === "planter";
      if (isFoliage) {
        // Solid underpaint so grass tile seams never show through leaf gaps.
        // Match dark forest canopy (not lawn yellow-green).
        // Compact underpaint — avoid large dark ink-blot ellipses under foliage
        const canopyH = rect.height * (kind === "bush" || kind === "hedge" ? 0.55 : 0.48);
        const canopyCy = rect.y + rect.height - canopyH * 0.5;
        const canopyRx = rect.width * (kind === "pine" ? 0.24 : 0.3);
        const canopyRy = canopyH * 0.38;
        ctx.fillStyle = "#0E301C";
        ctx.beginPath();
        ctx.ellipse(center.x, canopyCy, canopyRx, canopyRy, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(12, 16, 10, 0.32)";
        ctx.beginPath();
        ctx.ellipse(
          center.x + 1,
          center.y,
          Math.max(5, rect.width * 0.12),
          Math.max(2, rect.height * 0.035),
          0,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      // Opaque composite — never blend foliage with grass under partial alpha.
      const prevAlpha = ctx.globalAlpha;
      const prevSmooth = ctx.imageSmoothingEnabled;
      ctx.globalAlpha = 1;
      ctx.imageSmoothingEnabled = false; // crisp pixels; no soft holes from filtering
      ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
      ctx.imageSmoothingEnabled = prevSmooth;
      ctx.globalAlpha = prevAlpha;
      return true;
    } catch {
      return false;
    }
  }

  function warmFurnitureSprites() {
    const srcs = collectEssentialSpriteSrcs().concat(
      collectLayoutSpriteSrcs(currentLayout.furniture || []),
    );
    for (const f of furn) {
      if (!f || !f.kind) continue;
      const r = resolveFurnitureSprite(f.kind, f.dir || "S", f.style);
      if (r.asset) srcs.push(r.asset.src);
    }
    for (const f of decorFurn) {
      if (!f || !f.kind) continue;
      const r = resolveFurnitureSprite(f.kind, f.dir || "S", f.style);
      if (r.asset) srcs.push(r.asset.src);
    }
    warmSprites(srcs);
  }

  // Kinds that must NOT get an auto contact shadow: structural geometry, big
  // building facades (planted on their own edge), flat floor decals, and foliage
  // (which already paints its own underpaint + ground ellipse in the sprite path).
  const NO_PROP_SHADOW = new Set([
    "wall",
    "gwall",
    "gwallW",
    "door",
    "frame",
    "platform",
    "building",
    "rug",
    "mat",
    "tree",
    "pine",
    "bush",
    "hedge",
    "plant",
    "planter",
  ]);

  /**
   * Soft ground ellipse under a prop's footprint so it reads as sitting ON the floor
   * instead of pasted on top — the same trick avatars already use. Drawn just before
   * the prop in the depth-sorted pass. Especially load-bearing indoors, where props
   * sit on a pale tile floor with no other grounding cue.
   */
  function drawPropShadow(ctx, f) {
    if (!f || NO_PROP_SHADOW.has(f.kind)) return;
    const w = f.w > 0 ? f.w : 1;
    const d = f.d > 0 ? f.d : 1;
    const c = iso(f.gx + w / 2, f.gy + d / 2);
    const rx = Math.max(9, w * TW * 0.26);
    const ry = Math.max(4, d * TH * 0.26);
    ctx.save();
    ctx.fillStyle = "rgba(0,24,71,0.16)";
    ctx.beginPath();
    ctx.ellipse(c.x, c.y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Nymbl HQ — procedural brand-colored office (real nymbl.app palette): three
  // wings (sky / royal-blue / aqua) with a glass grid, navy roof + HVAC, a glass
  // entrance, and a white rooftop sign carrying the real Nymbl fox logo + wordmark.
  function drawNymblHQ(ctx, gx, gy, bw, bd, f) {
    const NAVY = "#111C46",
      SKY = "#29A7DE",
      ROYAL = "#4334DC",
      AQUA = "#7AC9AA",
      PANEL = "#F1FCFF";
    const H = 165; // body height in px — towers over a ~60px person, stays proportional

    // Base volume (navy core) + roof lip.
    box(ctx, gx, gy, bw, bd, H, NAVY, { left: shade(NAVY, -16), right: shade(NAVY, -8) });
    box(ctx, gx - 0.06, gy - 0.06, bw + 0.12, bd + 0.12, H + 8, shade(NAVY, 14), {
      left: shade(NAVY, 2),
      right: shade(NAVY, 22),
    });

    // Front (SW / +y) face: three brand wings + glass grid + center entrance.
    const W = bw * 32; // front-face width in local px (TW/2 per tile)
    const third = W / 3;
    faceSW(ctx, gx, gy + bd, H, (c) => {
      const wings = [SKY, ROYAL, AQUA];
      for (let i = 0; i < 3; i++) {
        c.fillStyle = wings[i];
        c.fillRect(i * third, 0, Math.ceil(third) + 1, H);
        c.fillStyle = "rgba(0,0,0,0.10)"; // seam between wings
        c.fillRect((i + 1) * third - 1, 0, 1.5, H);
      }
      // Glass window grid — 2 rows × 2 cols per wing (center wing bottom = entrance).
      const winW = third * 0.3;
      const winH = H * 0.22;
      for (let wi = 0; wi < 3; wi++) {
        for (let r = 0; r < 2; r++) {
          for (let cc = 0; cc < 2; cc++) {
            if (wi === 1 && r === 1) continue; // center-bottom reserved for the door
            const wx = wi * third + third * 0.17 + cc * (third * 0.43);
            const wy = H * 0.15 + r * (H * 0.34);
            c.fillStyle = "rgba(190,232,255,0.92)";
            c.fillRect(wx, wy, winW, winH);
            c.fillStyle = "rgba(255,255,255,0.45)";
            c.fillRect(wx + 1.5, wy + 1.5, winW * 0.34, winH * 0.34);
            c.fillStyle = "rgba(17,28,70,0.20)";
            c.fillRect(wx, wy + winH - 2, winW, 2);
          }
        }
      }
      // Glass entrance + awning on the center wing.
      const ew = third * 0.46;
      const ex = third + (third - ew) / 2;
      const ey = H * 0.54;
      const eh = H * 0.46;
      c.fillStyle = "rgba(90,120,155,0.9)";
      c.fillRect(ex - 2, ey - 2, ew + 4, eh + 2);
      c.fillStyle = "rgba(150,190,220,0.92)";
      c.fillRect(ex, ey, ew, eh);
      c.fillStyle = "rgba(17,28,70,0.5)";
      c.fillRect(ex + ew / 2 - 0.5, ey, 1, eh);
      c.fillStyle = "#CFD6E2";
      c.fillRect(ex - 4, ey - 5, ew + 8, 5);
    });

    // Right (SE / +x) face: navy with a couple of glass window rows.
    const A = iso(gx + bw, gy);
    const B = iso(gx + bw, gy + bd);
    const rcols = Math.max(2, Math.round(bd * 0.8));
    for (let r = 0; r < 2; r++) {
      const winY = H * 0.22 + r * (H * 0.34);
      for (let i = 0; i < rcols; i++) {
        const tt = (i + 0.5) / rcols;
        const px = A.x + (B.x - A.x) * tt;
        const py = A.y + (B.y - A.y) * tt - winY;
        ctx.fillStyle = "rgba(120,175,215,0.5)";
        ctx.fillRect(px - 4, py - 12, 8, 13);
      }
    }

    // Rooftop HVAC units.
    box(ctx, gx + bw * 0.2, gy + bd * 0.25, 0.7, 0.6, H + 16, "#5A6478", {
      left: "#474F60",
      right: "#6B7488",
    });
    box(ctx, gx + bw * 0.55, gy + bd * 0.4, 0.6, 0.5, H + 14, "#5A6478", {
      left: "#474F60",
      right: "#6B7488",
    });

    // Rooftop sign — a horizontal white nameplate floating just above the roof,
    // carrying the real Nymbl fox logo + wordmark (screen-space so it stays a clean
    // horizontal plate and never paints over the front wings).
    const roof = iso(gx + bw / 2, gy + bd / 2);
    const panelW = Math.round(bw * 13);
    const panelH = 30;
    const px = roof.x - panelW / 2;
    const py = roof.y - H - 42;
    ctx.fillStyle = "#0B1430";
    ctx.fillRect(roof.x - 3, py + panelH, 6, 14); // support post to the roof
    ctx.fillRect(px - 3, py - 3, panelW + 6, panelH + 6); // frame
    ctx.fillStyle = PANEL;
    ctx.fillRect(px, py, panelW, panelH); // white plate
    const logo = ensureSprite("/assets/nymbl-fox-blue.png");
    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "800 16px Montserrat, sans-serif";
    const word = "Nymbl";
    const wordW = ctx.measureText(word).width;
    const foxSize = 20;
    const gap = 5;
    const hasLogo = !!logo?.naturalWidth;
    const totalW = (hasLogo ? foxSize + gap : 0) + wordW;
    let tx = roof.x - totalW / 2;
    const cyy = py + panelH / 2;
    if (hasLogo) {
      ctx.drawImage(logo, tx, cyy - foxSize / 2, foxSize, foxSize);
      tx += foxSize + gap;
    }
    ctx.fillStyle = NAVY;
    ctx.fillText(word, tx, cyy);
    ctx.restore();
  }

  function drawFurniture(ctx, f, t, meetingActive) {
    if (tryDrawFurnitureSprite(ctx, f)) return;
    const { gx, gy, w, d } = f;
    switch (f.kind) {
      case "desk": {
        // dir = which way the monitor faces (toward the sitter)
        const ddir = f.dir || "N";
        box(ctx, gx, gy, w, d, 22, P.cloud, { left: P.stone, right: shade(P.cloud, -8) });
        // Monitor on the edge facing the sitter
        let mx = gx + 0.15,
          my = gy + 0.1,
          kx = gx + 1.25,
          ky = gy + 0.35;
        if (ddir === "S") {
          mx = gx + 0.15;
          my = gy + 0.55;
          kx = gx + 1.2;
          ky = gy + 0.2;
        } else if (ddir === "E") {
          mx = gx + 0.55;
          my = gy + 0.15;
          kx = gx + 0.15;
          ky = gy + 0.55;
        } else if (ddir === "W") {
          mx = gx + 0.05;
          my = gy + 0.15;
          kx = gx + 0.55;
          ky = gy + 0.55;
        }
        box(ctx, mx, my, 0.9, 0.16, 44, P.slate);
        const D = iso(mx + 0.05, my + 0.12);
        ctx.save();
        ctx.translate(D.x, D.y - 44);
        ctx.transform(1, 0.5, 0, 1, 0, 0);
        ctx.fillStyle = "#0E2C55";
        ctx.fillRect(0, 0, 28, 17);
        ctx.fillStyle = P.cyan;
        ctx.fillRect(2, 2, 24, 2);
        ctx.fillStyle = "rgba(255,255,255,.75)";
        ctx.fillRect(2, 6, 14 + 6 * Math.sin(t / 700 + gx), 1.5);
        ctx.fillRect(2, 9, 10 + 5 * Math.sin(t / 900 + gy), 1.5);
        ctx.restore();
        box(ctx, kx, ky, 0.55, 0.35, 25, P.paper);
        break;
      }
      case "chair": {
        // dir = which way the sitter faces; backrest is on the opposite side
        const cdir = f.dir || "S";
        box(ctx, gx + 0.25, gy + 0.25, 0.5, 0.5, 13, "#3E76B4", {
          left: "#28568C",
          right: "#336699",
        });
        if (cdir === "S") {
          // Face south → backrest on north edge
          box(ctx, gx + 0.25, gy + 0.1, 0.5, 0.14, 26, "#28568C");
        } else if (cdir === "N") {
          box(ctx, gx + 0.25, gy + 0.62, 0.5, 0.14, 26, "#28568C");
        } else if (cdir === "E") {
          box(ctx, gx + 0.1, gy + 0.25, 0.14, 0.5, 26, "#28568C");
        } else {
          // W
          box(ctx, gx + 0.62, gy + 0.25, 0.14, 0.5, 26, "#28568C");
        }
        break;
      }
      case "pod": {
        box(ctx, gx, gy - 0.04, 1, 0.22, 46, P.navy, { left: P.navyDeep, right: "#0A2B66" });
        box(ctx, gx - 0.02, gy, 0.2, 1, 40, "#0A2B66");
        box(ctx, gx + 0.82, gy, 0.2, 1, 40, "#0A2B66");
        box(ctx, gx + 0.2, gy + 0.3, 0.6, 0.5, 12, P.indigo);
        break;
      }
      case "couch": {
        // dir = sitter faces; backrest on opposite edge
        const cA = P.blue,
          first = f.part === 0,
          last = f.part === 2,
          cdir = f.dir || "S";
        if (cdir === "N") {
          box(ctx, gx, gy + 0.05, 1, 0.7, 12, cA, { left: shade(cA, -34), right: shade(cA, -16) });
          box(ctx, gx, gy + 0.7, 1, 0.22, 26, shade(cA, -12));
        } else {
          // S (default)
          box(ctx, gx, gy + 0.15, 1, 0.7, 12, cA, { left: shade(cA, -34), right: shade(cA, -16) });
          box(ctx, gx, gy, 1, 0.22, 26, shade(cA, -12));
        }
        if (first) box(ctx, gx - 0.08, gy, 0.16, 0.86, 18, shade(cA, -22));
        if (last) box(ctx, gx + 0.92, gy, 0.16, 0.86, 18, shade(cA, -22));
        break;
      }
      case "sofa1": {
        // dir = sitter faces; arm/back on opposite side
        const sdir = f.dir || "E";
        box(ctx, gx + 0.1, gy + 0.1, 0.8, 0.8, 12, P.indigo);
        if (sdir === "W") {
          box(ctx, gx + 0.7, gy + 0.02, 0.2, 0.96, 22, shade(P.indigo, -20));
        } else if (sdir === "S") {
          box(ctx, gx + 0.1, gy + 0.02, 0.96, 0.2, 22, shade(P.indigo, -20));
        } else if (sdir === "N") {
          box(ctx, gx + 0.1, gy + 0.7, 0.96, 0.2, 22, shade(P.indigo, -20));
        } else {
          // E — back on west
          box(ctx, gx + 0.1, gy + 0.02, 0.2, 0.96, 22, shade(P.indigo, -20));
        }
        break;
      }
      case "table": {
        // Dining/café/huddle table — waist-high (~0.4 person). Was 9px (near-flat).
        box(ctx, gx, gy + 0.15, w, 0.7, 24, P.paper, { left: P.stone, right: P.cloud });
        ctx.fillStyle = P.cyan;
        const c = iso(gx + 0.5, gy + 0.5);
        ctx.fillRect(c.x - 4, c.y - 28, 8, 5);
        break;
      }
      case "reception": {
        // Procedural fallback if hero PNG missing — curved desk approximated as counter
        box(ctx, gx, gy + 0.1, 1, 0.8, 26, P.indigo, {
          left: shade(P.indigo, -20),
          right: P.purple,
        });
        box(ctx, gx, gy + 0.1, 1, 0.8, 28, P.paper);
        box(ctx, gx + 0.35, gy + 0.25, 0.35, 0.3, 36, "#22355C");
        break;
      }
      case "coffeetable": {
        // Low lounge table (~knee height). Procedural fallback for the hero sprite.
        box(ctx, gx + 0.15, gy + 0.15, 0.7, 0.7, 14, P.wood || "#A87848", {
          left: shade("#A87848", -20),
          right: shade("#A87848", -8),
        });
        break;
      }
      case "counter": {
        box(ctx, gx, gy, w, 0.8, 26, P.navy, { left: P.navyDeep, right: "#0A2B66" });
        box(ctx, gx, gy, w, 0.8, 28, P.paper);
        box(ctx, gx + 1.2, gy + 0.2, 0.5, 0.4, 44, "#22355C");
        const D = iso(gx + 2.2, gy + 0.5);
        ctx.fillStyle = P.red;
        ctx.fillRect(D.x, D.y - 40, 3, 3);
        box(ctx, gx + 0.25, gy + 0.25, 0.22, 0.22, 32, P.white);
        box(ctx, gx + 0.55, gy + 0.35, 0.22, 0.22, 32, P.sky);
        break;
      }
      case "cooler": {
        box(ctx, gx + 0.25, gy + 0.25, 0.5, 0.5, 26, P.sky, { left: P.stone, right: P.cloud });
        box(ctx, gx + 0.32, gy + 0.32, 0.36, 0.36, 38, "rgba(45,155,214,0.55)");
        break;
      }
      case "plant": {
        box(ctx, gx + 0.3, gy + 0.3, 0.4, 0.4, 10, P.navy, { left: P.navyDeep, right: "#0A2B66" });
        const c = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = "#2EA86A";
        [
          [0, -24, 10, 10],
          [-8, -18, 9, 9],
          [3, -16, 9, 9],
          [-3, -30, 8, 8],
        ].forEach(([dx, dy, w2, h2], i) => {
          ctx.fillStyle = i % 2 ? "#1F7A4C" : "#2EA86A";
          ctx.fillRect(c.x + dx - w2 / 2, c.y + dy, w2, h2);
        });
        break;
      }
      case "conftable": {
        // All-hands boardroom table — desk-height (~0.4 person) so seated avatars
        // read correctly around it.
        box(ctx, gx, gy + 0.1, w, d - 0.2, 25, P.paper, { left: P.stone, right: P.cloud });
        const c = iso(gx + w / 2, gy + d / 2);
        ctx.fillStyle = P.slate;
        ctx.fillRect(c.x - 8, c.y - 28, 16, 6);
        break;
      }
      case "wall": {
        // Multi-tile wall segments (campus building shells use w×d > 1).
        const ww = Math.max(1, w);
        const wd = Math.max(1, d);
        const wallFill = f.c || P.mist;
        box(ctx, gx, gy, ww, wd, 76, wallFill, {
          left: shade(wallFill, -20),
          right: shade(wallFill, -35),
        });
        if (f.scr)
          faceSW(ctx, gx, gy + 1, 66, (c2) => {
            c2.fillStyle = "#06122E";
            c2.fillRect(2, 6, 30, 20);
            if (meetingActive) {
              c2.fillStyle = P.cyan;
              c2.fillRect(4, 8, 12, 1.6);
              for (let i = 0; i < 4; i++) {
                const h2 = 4 + (Math.sin(t / 500 + gx + i) + 1) * 4;
                c2.fillStyle = [P.cyan, P.blue, P.indigo, P.purple][i];
                c2.fillRect(5 + i * 6, 23 - h2, 4, h2);
              }
            } else {
              c2.fillStyle = "rgba(255,255,255,0.35)";
              c2.fillRect(4, 12, 16, 2);
              c2.fillRect(4, 16, 10, 2);
            }
          });
        break;
      }
      case "gwall":
      case "gwallW": {
        const west = f.kind === "gwallW";
        // Multi-tile glass facades (campus buildings) draw as a single panel.
        const gw = Math.max(1, w);
        const gd = Math.max(1, d);
        // Personal-office facades frost when the door is closed (social privacy cue).
        const facadeClosed =
          f.facade && f.slotKey && personalOfficeState.get(f.slotKey)?.doorStatus !== "open";
        const glassFill = facadeClosed ? "rgba(170,195,220,0.52)" : "rgba(45,155,214,0.28)";
        const A = iso(gx, gy),
          B = iso(gx + (west ? 1 : gw), gy),
          C = iso(gx + (west ? 1 : gw), gy + (west ? gd : 1)),
          D = iso(gx, gy + (west ? gd : 1));
        const h = 62;
        if (west) {
          poly(
            ctx,
            [
              [C.x, C.y],
              [B.x, B.y],
              [B.x, B.y - h],
              [C.x, C.y - h],
            ],
            glassFill,
          );
          poly(
            ctx,
            [
              [C.x, C.y],
              [B.x, B.y],
              [B.x, B.y - 8],
              [C.x, C.y - 8],
            ],
            P.navy,
          );
          poly(
            ctx,
            [
              [C.x, C.y - h],
              [B.x, B.y - h],
              [B.x, B.y - h - 4],
              [C.x, C.y - h - 4],
            ],
            P.navy,
          );
        } else {
          poly(
            ctx,
            [
              [D.x, D.y],
              [C.x, C.y],
              [C.x, C.y - h],
              [D.x, D.y - h],
            ],
            glassFill,
          );
          poly(
            ctx,
            [
              [D.x, D.y],
              [C.x, C.y],
              [C.x, C.y - 8],
              [D.x, D.y - 8],
            ],
            P.navy,
          );
          poly(
            ctx,
            [
              [D.x, D.y - h],
              [C.x, C.y - h],
              [C.x, C.y - h - 4],
              [D.x, D.y - h - 4],
            ],
            P.navy,
          );
        }
        break;
      }
      case "platform": {
        box(ctx, gx, gy, w, d, 13, "#0A2B66", { left: P.navyDeep, right: "#07204D" });
        const D = iso(gx, gy + d);
        poly(
          ctx,
          [
            [D.x - 20, D.y + 3],
            [D.x, D.y - 7],
            [D.x + 20, D.y + 3],
            [D.x, D.y + 13],
          ],
          "#123B7E",
        );
        break;
      }
      case "screen": {
        box(ctx, gx, gy, w, 0.3, 104, P.navy, { left: P.navyDeep, right: "#0A2B66" });
        faceSW(ctx, gx, gy + 0.3, 91, (c2) => {
          c2.fillStyle = "#040E24";
          c2.fillRect(3, 13 + 5, w * 32 - 6, 52);
          const g = c2.createLinearGradient(6, 0, w * 32 - 12, 0);
          g.addColorStop(0, P.cyan);
          g.addColorStop(0.4, P.blue);
          g.addColorStop(0.7, P.indigo);
          g.addColorStop(1, P.purple);
          c2.fillStyle = g;
          c2.fillRect(8, 24, w * 32 - 16, 4);
          c2.fillStyle = P.white;
          c2.font = "700 11px Montserrat, sans-serif";
          c2.fillText("ALL HANDS", 8, 42);
          c2.font = "600 8px Raleway, sans-serif";
          c2.fillStyle = "rgba(255,255,255,0.7)";
          c2.fillText("FRIDAY \u00B7 4:00 PM", 8, 54);
        });
        break;
      }
      case "podium": {
        box(ctx, gx + 0.28, gy + 0.28, 0.44, 0.44, 32, P.blue, { left: shade(P.blue, -30) });
        break;
      }
      case "cushion": {
        box(ctx, gx + 0.2, gy + 0.2, 0.6, 0.6, 6, f.c || P.cyan);
        break;
      }
      case "fox": {
        box(ctx, gx + 0.15, gy + 0.15, 0.7, 0.7, 12, P.stone, { left: P.slate, right: P.cloud });
        const c = iso(gx + 0.5, gy + 0.5),
          u2 = 2;
        FOX.forEach((row, ry) => {
          for (let rx = 0; rx < row.length; rx++) {
            const ch = row[rx];
            if (ch === ".") continue;
            ctx.fillStyle = ch === "w" ? P.white : P.navy;
            ctx.fillRect(c.x + (rx - 6.5) * u2, c.y - 12 - (FOX.length - ry) * u2, u2, u2);
          }
        });
        break;
      }
      case "door": {
        // Personal-office plaque (open/closed) OR campus entrance (soft glow only —
        // never a solid black cube; the facade art already shows the doorway).
        const st = f.slotKey ? personalOfficeState.get(f.slotKey) : null;
        const isCampusDoor = !f.slotKey;
        const open = isCampusDoor ? true : st?.doorStatus === "open";
        const label = st?.displayName || f.displayName || f.slotKey || "Enter";
        const doorGlow = {
          cafe: "rgba(80, 170, 255, 0.4)",
          people_marketing: "rgba(60, 230, 140, 0.4)",
          delivery: "rgba(255, 170, 60, 0.38)",
          post_delivery: "rgba(160, 120, 255, 0.4)",
          customer_success: "rgba(80, 255, 160, 0.4)",
          reception: "rgba(100, 190, 255, 0.38)",
        };
        const glow = doorGlow[f.style] || (open ? "rgba(46, 168, 106, 0.35)" : null);
        const dp = iso(gx + 0.5, gy + 0.5);
        if (isCampusDoor && glow) {
          // Soft ground shimmer + floating label (village-ref "click to enter")
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.ellipse(dp.x, dp.y - 2, 14, 8, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = glow
            .replace("0.4", "0.18")
            .replace("0.38", "0.16")
            .replace("0.35", "0.15");
          ctx.beginPath();
          ctx.ellipse(dp.x, dp.y - 10, 10, 14, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          box(ctx, gx + 0.15, gy + 0.35, 0.7, 0.3, 4, open ? "#2EA86A" : P.slate);
        }
        ctx.save();
        ctx.textAlign = "center";
        if (isCampusDoor) {
          ctx.font = "700 9px Montserrat, sans-serif";
          ctx.fillStyle = "rgba(0, 24, 71, 0.88)";
          ctx.fillText(String(label).slice(0, 16), dp.x, dp.y - 40);
          ctx.font = "600 8px Raleway, sans-serif";
          ctx.fillStyle = P.green;
          ctx.fillText("ENTER", dp.x, dp.y - 28);
        } else {
          ctx.fillStyle = open ? P.green : P.navy;
          ctx.font = "700 9px Montserrat, sans-serif";
          ctx.fillText(String(label).slice(0, 14), dp.x, dp.y - 36);
          ctx.font = "600 7px Raleway, sans-serif";
          ctx.fillStyle = open ? P.green : P.red;
          ctx.fillText(open ? "OPEN" : "CLOSED", dp.x, dp.y - 24);
        }
        ctx.restore();
        break;
      }
      case "sign": {
        box(ctx, gx + 0.2, gy + 0.35, 0.6, 0.2, 36, P.navy);
        faceSW(ctx, gx, gy + 1, 30, (c2) => {
          c2.fillStyle = P.cyan;
          c2.font = "700 8px Montserrat, sans-serif";
          c2.fillText("VYLLAGE", 4, 14);
        });
        break;
      }
      case "arcade": {
        box(ctx, gx + 0.2, gy + 0.2, 0.6, 0.6, 40, P.indigo, {
          left: shade(P.indigo, -20),
          right: shade(P.indigo, -10),
        });
        box(ctx, gx + 0.28, gy + 0.28, 0.44, 0.3, 48, "#06122E");
        break;
      }
      case "trophy": {
        box(ctx, gx + 0.3, gy + 0.3, 0.4, 0.4, 10, P.amber);
        const tc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = P.amber;
        ctx.beginPath();
        ctx.arc(tc.x, tc.y - 22, 6, 0, 7);
        ctx.fill();
        break;
      }
      case "lamp": {
        box(ctx, gx + 0.35, gy + 0.35, 0.3, 0.3, 8, P.slate);
        box(ctx, gx + 0.42, gy + 0.42, 0.16, 0.16, 28, P.stone);
        box(ctx, gx + 0.28, gy + 0.28, 0.44, 0.44, 36, P.amber, {
          left: shade(P.amber, -25),
        });
        break;
      }
      case "building": {
        // Multi-story outdoor shell — taller than office walls, window bands, roof lip, name plate.
        const bw = Math.max(2, w);
        const bd = Math.max(2, d);
        if (f.style === "nymbl_hq") {
          drawNymblHQ(ctx, gx, gy, bw, bd, f);
          break;
        }
        const body = f.c || P.blue;
        const stories = bw * bd >= 80 ? 3 : bw * bd >= 40 ? 2 : 2;
        const h = 42 + stories * 28;
        // Main volume
        box(ctx, gx, gy, bw, bd, h, body, {
          left: shade(body, -28),
          right: shade(body, -14),
        });
        // Roof lip (slightly larger, lighter)
        box(ctx, gx - 0.08, gy - 0.08, bw + 0.16, bd + 0.16, h + 10, shade(body, 18), {
          left: shade(body, -10),
          right: shade(body, 6),
        });
        // Parapet stripe
        box(ctx, gx + 0.05, gy + 0.05, bw - 0.1, bd - 0.1, h + 14, shade(body, -40), {
          left: shade(body, -50),
          right: shade(body, -30),
        });
        // Window bands on the two visible iso faces
        for (let story = 0; story < stories; story++) {
          const winY = 18 + story * 26;
          faceSW(ctx, gx, gy + bd, h - 8, (c2) => {
            const cols = Math.max(2, Math.floor(bw * 1.6));
            for (let i = 0; i < cols; i++) {
              const wx = 6 + i * ((bw * 28) / cols);
              c2.fillStyle = "rgba(180, 220, 255, 0.55)";
              c2.fillRect(wx, winY, 10, 12);
              c2.fillStyle = "rgba(255, 255, 255, 0.25)";
              c2.fillRect(wx + 1, winY + 1, 4, 4);
            }
          });
          // SE-ish face via rotated approximation (right face of iso box)
          const A = iso(gx + bw, gy),
            B = iso(gx + bw, gy + bd);
          const midX = (A.x + B.x) / 2;
          const baseY = (A.y + B.y) / 2;
          const cols2 = Math.max(2, Math.floor(bd * 1.4));
          for (let i = 0; i < cols2; i++) {
            const t = (i + 0.5) / cols2;
            const px = A.x + (B.x - A.x) * t;
            const py = A.y + (B.y - A.y) * t - winY - 4;
            ctx.fillStyle = "rgba(160, 210, 245, 0.5)";
            ctx.fillRect(px - 4, py, 8, 11);
          }
          void midX;
          void baseY;
        }
        // Soft portal on the door wall (never solid black — that read as "black cubes")
        const doorWall = f.doorWall || "S";
        const doorMidX = gx + bw / 2;
        const doorMidY = gy + bd / 2;
        const portal = "rgba(70, 150, 220, 0.5)";
        const portalDeep = "rgba(40, 90, 150, 0.55)";
        if (doorWall === "S") {
          box(ctx, doorMidX - 0.45, gy + bd - 0.15, 0.9, 0.2, 30, portalDeep, {
            left: shade(body, -35),
            right: shade(body, -10),
          });
          box(ctx, doorMidX - 0.35, gy + bd - 0.12, 0.7, 0.15, 26, portal);
        } else if (doorWall === "N") {
          box(ctx, doorMidX - 0.45, gy - 0.05, 0.9, 0.2, 30, portalDeep);
          box(ctx, doorMidX - 0.35, gy, 0.7, 0.15, 26, portal);
        } else if (doorWall === "E") {
          box(ctx, gx + bw - 0.15, doorMidY - 0.45, 0.2, 0.9, 30, portalDeep);
          box(ctx, gx + bw - 0.12, doorMidY - 0.35, 0.15, 0.7, 26, portal);
        } else {
          box(ctx, gx - 0.05, doorMidY - 0.45, 0.2, 0.9, 30, portalDeep);
          box(ctx, gx, doorMidY - 0.35, 0.15, 0.7, 26, portal);
        }
        // Large rooftop billboard — clear building identity (Habbo-style roof sign).
        const rawName = String(f.displayName || "Building").trim() || "Building";
        // Split long names onto two lines for readability (e.g. "People + Marketing").
        let line1 = rawName.toUpperCase();
        let line2 = "";
        if (rawName.length > 12) {
          const parts = rawName.split(/\s+/);
          if (parts.length >= 2) {
            const mid = Math.ceil(parts.length / 2);
            line1 = parts.slice(0, mid).join(" ").toUpperCase();
            line2 = parts.slice(mid).join(" ").toUpperCase();
          } else {
            line1 = rawName.slice(0, 12).toUpperCase();
            line2 = rawName.slice(12, 24).toUpperCase();
          }
        }
        // 3D sign board mounted on the roof ridge (grid units, centered).
        const signW = Math.min(bw * 0.85, Math.max(3.2, bw * 0.55));
        const signD = Math.min(0.55, bd * 0.22);
        const signGx = gx + (bw - signW) / 2;
        const signGy = gy + (bd - signD) / 2;
        const signH = h + 22;
        // Dark frame
        box(ctx, signGx - 0.06, signGy - 0.04, signW + 0.12, signD + 0.08, signH + 4, "#0A1628", {
          left: "#061018",
          right: "#122038",
        });
        // Bright face panel
        box(ctx, signGx, signGy, signW, signD, signH + 18, "#F4F7FB", {
          left: "#D8DEE8",
          right: "#FFFFFF",
        });
        // Accent strip under text
        box(ctx, signGx + 0.08, signGy + 0.08, signW - 0.16, signD - 0.12, signH + 6, body, {
          left: shade(body, -20),
          right: shade(body, -8),
        });
        // Label text on the billboard face
        const roof = iso(gx + bw / 2, gy + bd / 2);
        const textY = roof.y - signH - 14;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        // Soft shadow for legibility over busy roofs
        ctx.fillStyle = "rgba(0, 16, 40, 0.35)";
        ctx.font = line2 ? "800 12px Montserrat, sans-serif" : "800 13px Montserrat, sans-serif";
        if (line2) {
          ctx.fillText(line1, roof.x + 1, textY - 6 + 1);
          ctx.fillText(line2, roof.x + 1, textY + 8 + 1);
          ctx.fillStyle = "#0A1628";
          ctx.fillText(line1, roof.x, textY - 6);
          ctx.fillText(line2, roof.x, textY + 8);
        } else {
          ctx.fillText(line1, roof.x + 1, textY + 1);
          ctx.fillStyle = "#0A1628";
          ctx.fillText(line1, roof.x, textY);
        }
        ctx.restore();
        break;
      }
      case "tree": {
        // Dark forest canopy + thick trunk so trees read on yellow-green lawn.
        const tc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = "rgba(20, 24, 18, 0.28)";
        ctx.beginPath();
        ctx.ellipse(tc.x + 2, tc.y + 2, 16, 7, 0, 0, Math.PI * 2);
        ctx.fill();
        box(ctx, gx + 0.38, gy + 0.38, 0.24, 0.24, 24, "#4A2E14", {
          left: "#2E1A0C",
          right: "#6A4220",
        });
        const layers = [
          [0, -34, 20, "#0E3A22"],
          [-9, -28, 15, "#14502C"],
          [9, -28, 15, "#0F4426"],
          [0, -46, 13, "#1A5A34"],
          [-6, -40, 11, "#0A301C"],
          [7, -42, 10, "#164C2A"],
        ];
        for (const [dx, dy, r, col] of layers) {
          ctx.fillStyle = "#061810";
          ctx.beginPath();
          ctx.ellipse(tc.x + dx, tc.y + dy, r + 1.5, r * 0.72 + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = col;
          ctx.beginPath();
          ctx.ellipse(tc.x + dx, tc.y + dy, r, r * 0.72, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case "bush": {
        const bc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = "rgba(20, 24, 18, 0.22)";
        ctx.beginPath();
        ctx.ellipse(bc.x + 1, bc.y + 2, 14, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        box(ctx, gx + 0.25, gy + 0.25, 0.5, 0.5, 6, "#1A3A18", {
          left: "#0E2410",
          right: "#2A4A20",
        });
        [
          [0, -10, 14],
          [-8, -6, 11],
          [8, -6, 11],
          [0, -16, 9],
        ].forEach(([dx, dy, r], i) => {
          ctx.fillStyle = "#061810";
          ctx.beginPath();
          ctx.ellipse(bc.x + dx, bc.y + dy, r + 1, r * 0.7 + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = i % 2 ? "#1A5A32" : "#0F4024";
          ctx.beginPath();
          ctx.ellipse(bc.x + dx, bc.y + dy, r, r * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
        });
        break;
      }
      case "bench": {
        // Park bench — wood slats on stone legs (sittable).
        const dir = f.dir || "S";
        box(ctx, gx + 0.1, gy + 0.25, 0.8, 0.45, 8, "#6B4A2A", {
          left: "#4A3218",
          right: "#8A6238",
        });
        box(ctx, gx + 0.12, gy + 0.28, 0.76, 0.12, 14, "#8B5A2B");
        box(ctx, gx + 0.12, gy + 0.48, 0.76, 0.12, 14, "#8B5A2B");
        // Backrest
        if (dir === "N") box(ctx, gx + 0.1, gy + 0.15, 0.8, 0.12, 18, "#5C3A1E");
        else if (dir === "S") box(ctx, gx + 0.1, gy + 0.55, 0.8, 0.12, 18, "#5C3A1E");
        else if (dir === "E") box(ctx, gx + 0.7, gy + 0.25, 0.15, 0.45, 18, "#5C3A1E");
        else box(ctx, gx + 0.1, gy + 0.25, 0.15, 0.45, 18, "#5C3A1E");
        break;
      }
      case "streetlamp": {
        // Tall outdoor lamp post with glowing head. Procedural fallback (the PNG
        // normally renders); scale it to the same person-anchored target height so
        // even the fallback towers over a person. 58 is the head's baseline height.
        const lampH = proceduralHeightPx("streetlamp") ?? 58;
        const s = lampH / 58;
        box(ctx, gx + 0.42, gy + 0.42, 0.16, 0.16, 8 * s, "#3A3A44");
        box(ctx, gx + 0.44, gy + 0.44, 0.12, 0.12, 52 * s, "#2A2A32", {
          left: "#1A1A22",
          right: "#3A3A48",
        });
        box(ctx, gx + 0.32, gy + 0.32, 0.36, 0.36, 58 * s, "#E8C96A", {
          left: shade("#E8C96A", -30),
          right: shade("#E8C96A", -10),
        });
        // Soft glow halo
        const lc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = "rgba(232, 201, 106, 0.22)";
        ctx.beginPath();
        ctx.arc(lc.x, lc.y - 56 * s, 16, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "fountain": {
        // Tiered plaza fountain with water pool.
        box(ctx, gx + 0.05, gy + 0.05, 0.9, 0.9, 8, P.stone, {
          left: shade(P.stone, -20),
          right: shade(P.stone, -8),
        });
        box(ctx, gx + 0.15, gy + 0.15, 0.7, 0.7, 14, "#5BA8D0", {
          left: "#3A88B0",
          right: "#7AC0E0",
        });
        box(ctx, gx + 0.3, gy + 0.3, 0.4, 0.4, 22, P.cloud, {
          left: P.stone,
          right: shade(P.cloud, -10),
        });
        box(ctx, gx + 0.38, gy + 0.38, 0.24, 0.24, 30, "#7AC8E8");
        const fc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = "rgba(180, 230, 255, 0.65)";
        ctx.beginPath();
        ctx.arc(fc.x, fc.y - 34, 5, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "plaza_sign": {
        // Wooden campus welcome board (NYMBL VILLAGE).
        box(ctx, gx + 0.42, gy + 0.42, 0.16, 0.16, 6, "#4A3218");
        box(ctx, gx + 0.44, gy + 0.44, 0.12, 0.12, 28, "#5C3A1E");
        box(ctx, gx + 0.12, gy + 0.25, 0.76, 0.4, 40, "#6B4A2A", {
          left: "#4A3218",
          right: "#8A6238",
        });
        faceSW(ctx, gx + 0.15, gy + 0.55, 36, (c2) => {
          c2.fillStyle = "#3A9A5A";
          c2.fillRect(4, 4, 10, 10);
          c2.fillStyle = "#E8D4A8";
          c2.font = "700 8px Montserrat, sans-serif";
          c2.fillText("NYMBL", 16, 12);
          c2.font = "600 7px Raleway, sans-serif";
          c2.fillText("VILLAGE", 14, 24);
        });
        break;
      }
      case "cafe_umbrella": {
        // Patio table + red/white striped umbrella.
        box(ctx, gx + 0.25, gy + 0.25, 0.5, 0.5, 10, "#8B5A2B", {
          left: "#5C3A1E",
          right: "#A07040",
        });
        box(ctx, gx + 0.42, gy + 0.42, 0.16, 0.16, 28, "#E8E0D4");
        const uc = iso(gx + 0.5, gy + 0.5);
        // Canopy
        for (let i = 0; i < 6; i++) {
          ctx.fillStyle = i % 2 ? "#E8E8E8" : "#D04040";
          ctx.beginPath();
          ctx.moveTo(uc.x, uc.y - 42);
          const a0 = (i / 6) * Math.PI * 2;
          const a1 = ((i + 1) / 6) * Math.PI * 2;
          ctx.lineTo(uc.x + Math.cos(a0) * 20, uc.y - 28 + Math.sin(a0) * 8);
          ctx.lineTo(uc.x + Math.cos(a1) * 20, uc.y - 28 + Math.sin(a1) * 8);
          ctx.closePath();
          ctx.fill();
        }
        break;
      }
      case "delivery_truck": {
        // Orange delivery van on the warehouse apron.
        const tw = Math.max(1.4, w);
        const td = Math.max(0.7, d);
        box(ctx, gx, gy + 0.1, tw * 0.65, td, 22, "#E09030", {
          left: "#B07020",
          right: "#F0A848",
        });
        box(ctx, gx + tw * 0.55, gy + 0.15, tw * 0.4, td * 0.9, 18, "#F0B060", {
          left: "#C08030",
          right: "#F8C878",
        });
        // Cab window
        box(ctx, gx + tw * 0.62, gy + 0.25, tw * 0.22, td * 0.55, 26, "rgba(140, 200, 240, 0.7)");
        // Wheels
        box(ctx, gx + 0.15, gy + td * 0.7, 0.25, 0.2, 6, "#2A2A32");
        box(ctx, gx + tw * 0.7, gy + td * 0.7, 0.25, 0.2, 6, "#2A2A32");
        break;
      }
      case "pine": {
        const pc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = "rgba(20, 24, 18, 0.28)";
        ctx.beginPath();
        ctx.ellipse(pc.x + 2, pc.y + 2, 12, 5, 0, 0, Math.PI * 2);
        ctx.fill();
        box(ctx, gx + 0.4, gy + 0.4, 0.2, 0.2, 18, "#3E2614");
        [
          [0, -28, 16],
          [0, -40, 12],
          [0, -50, 8],
        ].forEach(([dx, dy, r], i) => {
          ctx.fillStyle = "#041018";
          ctx.beginPath();
          ctx.moveTo(pc.x + dx, pc.y + dy - r - 1);
          ctx.lineTo(pc.x + dx - r - 1, pc.y + dy + r * 0.6 + 1);
          ctx.lineTo(pc.x + dx + r + 1, pc.y + dy + r * 0.6 + 1);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = i % 2 ? "#0A3A38" : "#0C4840";
          ctx.beginPath();
          ctx.moveTo(pc.x + dx, pc.y + dy - r);
          ctx.lineTo(pc.x + dx - r, pc.y + dy + r * 0.6);
          ctx.lineTo(pc.x + dx + r, pc.y + dy + r * 0.6);
          ctx.closePath();
          ctx.fill();
        });
        break;
      }
      case "rock": {
        box(ctx, gx + 0.15, gy + 0.2, 0.7, 0.55, 14, "#8A8A90", {
          left: "#6A6A70",
          right: "#A0A0A8",
        });
        box(ctx, gx + 0.28, gy + 0.28, 0.4, 0.3, 20, "#9A9AA0");
        break;
      }
      case "flower": {
        box(ctx, gx + 0.42, gy + 0.42, 0.16, 0.16, 10, "#2EA86A");
        const fl = iso(gx + 0.5, gy + 0.5);
        ["#E85A7A", "#F0C040", "#7AB8F0", "#E85A7A"].forEach((col, i) => {
          const a = (i / 4) * Math.PI * 2;
          ctx.fillStyle = col;
          ctx.beginPath();
          ctx.ellipse(fl.x + Math.cos(a) * 6, fl.y - 16 + Math.sin(a) * 4, 4, 3, 0, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.fillStyle = "#F5E060";
        ctx.beginPath();
        ctx.arc(fl.x, fl.y - 16, 3, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "hedge": {
        box(ctx, gx + 0.05, gy + 0.15, 0.9, 0.7, 18, "#2A6A3A", {
          left: "#1A4A28",
          right: "#3A8A4A",
        });
        box(ctx, gx + 0.1, gy + 0.2, 0.8, 0.55, 26, "#2EA86A");
        break;
      }
      case "planter": {
        box(ctx, gx + 0.15, gy + 0.2, 0.7, 0.55, 12, "#8B5A2B", {
          left: "#5C3A1E",
          right: "#A07040",
        });
        box(ctx, gx + 0.25, gy + 0.28, 0.5, 0.4, 18, "#3A5A28");
        box(ctx, gx + 0.35, gy + 0.35, 0.3, 0.25, 28, "#2EA86A");
        break;
      }
      case "bollard": {
        box(ctx, gx + 0.35, gy + 0.35, 0.3, 0.3, 8, "#3A3A44");
        box(ctx, gx + 0.38, gy + 0.38, 0.24, 0.24, 26, "#2A2A32", {
          left: "#1A1A22",
          right: "#4A4A55",
        });
        box(ctx, gx + 0.36, gy + 0.36, 0.28, 0.28, 30, "#C8CCD4");
        break;
      }
      case "fence": {
        box(ctx, gx + 0.1, gy + 0.35, 0.15, 0.2, 22, "#6B4A2A");
        box(ctx, gx + 0.75, gy + 0.35, 0.15, 0.2, 22, "#6B4A2A");
        box(ctx, gx + 0.1, gy + 0.35, 0.8, 0.2, 12, "#8B5A2B");
        box(ctx, gx + 0.1, gy + 0.35, 0.8, 0.2, 20, "#8B5A2B");
        break;
      }
      case "flowerbed": {
        const fw = Math.max(1.5, w);
        const fd = Math.max(0.7, d);
        box(ctx, gx, gy + 0.15, fw, fd, 8, "#5C3A1E", { left: "#3E2614", right: "#7A4E28" });
        box(ctx, gx + 0.08, gy + 0.22, fw - 0.16, fd - 0.2, 12, "#4A3220");
        for (let i = 0; i < 5; i++) {
          const fx = gx + 0.15 + i * ((fw - 0.3) / 4);
          box(
            ctx,
            fx,
            gy + 0.3,
            0.18,
            0.18,
            18 + (i % 3) * 3,
            ["#E85A7A", "#F0C040", "#7AB8F0", "#C060E0", "#2EA86A"][i],
          );
        }
        break;
      }
      case "cactus": {
        box(ctx, gx + 0.3, gy + 0.3, 0.4, 0.4, 8, P.stone);
        box(ctx, gx + 0.38, gy + 0.38, 0.24, 0.24, 34, P.green, { left: shade(P.green, -25) });
        box(ctx, gx + 0.22, gy + 0.4, 0.14, 0.14, 18, P.green);
        box(ctx, gx + 0.62, gy + 0.4, 0.14, 0.14, 16, P.green);
        break;
      }
      case "bookshelf": {
        box(ctx, gx + 0.1, gy + 0.15, 0.8, 0.7, 48, P.cloud, {
          left: P.stone,
          right: shade(P.cloud, -12),
        });
        box(ctx, gx + 0.15, gy + 0.2, 0.7, 0.15, 12, P.blue);
        box(ctx, gx + 0.15, gy + 0.4, 0.7, 0.15, 12, P.indigo);
        box(ctx, gx + 0.15, gy + 0.6, 0.7, 0.15, 12, P.amber);
        break;
      }
      case "beanbag": {
        // Squat pouffe with a rounded top cushion — reads as a soft seat, not a cube.
        box(ctx, gx + 0.16, gy + 0.16, 0.68, 0.68, 9, P.purple, {
          left: shade(P.purple, -25),
          right: shade(P.purple, -12),
        });
        const bc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = shade(P.purple, 20);
        ctx.beginPath();
        ctx.ellipse(bc.x, bc.y - 9, 11, 5.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(12,20,40,0.42)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.strokeStyle = shade(P.purple, -32);
        ctx.beginPath();
        ctx.moveTo(bc.x - 8, bc.y - 9);
        ctx.lineTo(bc.x + 8, bc.y - 9);
        ctx.stroke();
        break;
      }
      case "whiteboard": {
        box(ctx, gx + 0.05, gy + 0.35, 0.9, 0.25, 40, P.paper, { left: P.slate, right: P.cloud });
        box(ctx, gx + 0.15, gy + 0.4, 0.7, 0.1, 28, "#EAF1F9");
        break;
      }
      case "coffee": {
        box(ctx, gx + 0.2, gy + 0.2, 0.6, 0.6, 18, P.cloud, { left: P.stone });
        box(ctx, gx + 0.3, gy + 0.3, 0.25, 0.25, 30, P.navy);
        box(ctx, gx + 0.55, gy + 0.35, 0.2, 0.2, 14, P.paper);
        break;
      }
      case "printer": {
        box(ctx, gx + 0.15, gy + 0.2, 0.7, 0.6, 20, P.slate, { left: shade(P.slate, -20) });
        box(ctx, gx + 0.25, gy + 0.28, 0.5, 0.35, 12, P.paper);
        break;
      }
      case "bin": {
        box(ctx, gx + 0.3, gy + 0.3, 0.4, 0.4, 18, P.slate, { left: P.graphite });
        break;
      }
      case "clock": {
        box(ctx, gx + 0.35, gy + 0.4, 0.3, 0.2, 28, P.navy);
        const cc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = P.paper;
        ctx.beginPath();
        ctx.arc(cc.x, cc.y - 22, 7, 0, 7);
        ctx.fill();
        ctx.strokeStyle = P.navy;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cc.x, cc.y - 22, 7, 0, 7);
        ctx.stroke();
        break;
      }
      case "fridge": {
        box(ctx, gx + 0.2, gy + 0.2, 0.6, 0.6, 44, P.cloud, { left: P.slate, right: P.paper });
        box(ctx, gx + 0.55, gy + 0.35, 0.12, 0.1, 20, P.cyan);
        break;
      }
      case "locker": {
        box(ctx, gx + 0.2, gy + 0.2, 0.6, 0.6, 40, P.blue, { left: shade(P.blue, -30) });
        box(ctx, gx + 0.55, gy + 0.35, 0.1, 0.1, 18, P.amber);
        break;
      }
      case "guitar": {
        box(ctx, gx + 0.35, gy + 0.4, 0.3, 0.2, 8, "#5C4033");
        box(ctx, gx + 0.4, gy + 0.35, 0.2, 0.15, 36, "#C9A227");
        break;
      }
      case "bike": {
        box(ctx, gx + 0.15, gy + 0.35, 0.7, 0.3, 6, P.slate);
        box(ctx, gx + 0.2, gy + 0.4, 0.2, 0.2, 14, P.navy);
        box(ctx, gx + 0.6, gy + 0.4, 0.2, 0.2, 14, P.navy);
        break;
      }
      case "fishbowl": {
        box(ctx, gx + 0.28, gy + 0.28, 0.44, 0.44, 10, P.stone);
        box(ctx, gx + 0.25, gy + 0.25, 0.5, 0.5, 24, "rgba(45,155,214,0.35)", {
          left: "rgba(45,155,214,0.25)",
        });
        break;
      }
      case "easel": {
        box(ctx, gx + 0.35, gy + 0.35, 0.3, 0.3, 8, P.stone);
        box(ctx, gx + 0.2, gy + 0.35, 0.6, 0.25, 34, P.paper, { left: P.cloud });
        break;
      }
      case "flag": {
        box(ctx, gx + 0.45, gy + 0.45, 0.1, 0.1, 40, P.slate);
        box(ctx, gx + 0.5, gy + 0.35, 0.35, 0.15, 34, P.cyan);
        break;
      }
      case "neon": {
        box(ctx, gx + 0.15, gy + 0.4, 0.7, 0.2, 28, P.navy);
        faceSW(ctx, gx, gy + 1, 24, (c2) => {
          c2.fillStyle = "#FF6B9D";
          c2.font = "700 9px Montserrat, sans-serif";
          c2.fillText("OPEN", 6, 14);
        });
        break;
      }
      case "mic": {
        box(ctx, gx + 0.4, gy + 0.4, 0.2, 0.2, 8, P.slate);
        box(ctx, gx + 0.42, gy + 0.42, 0.16, 0.16, 26, P.graphite);
        box(ctx, gx + 0.35, gy + 0.35, 0.3, 0.3, 32, P.navy);
        break;
      }
      case "stool": {
        box(ctx, gx + 0.3, gy + 0.3, 0.4, 0.4, 15, P.indigo, {
          left: shade(P.indigo, -25),
          right: shade(P.indigo, -12),
        });
        // Padded round seat cushion on top so it reads as a stool, not a block.
        const sc = iso(gx + 0.5, gy + 0.5);
        ctx.fillStyle = shade(P.indigo, 24);
        ctx.beginPath();
        ctx.ellipse(sc.x, sc.y - 15, 9, 4.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(12,20,40,0.42)";
        ctx.lineWidth = 1;
        ctx.stroke();
        break;
      }
      case "jukebox": {
        box(ctx, gx + 0.15, gy + 0.2, 0.7, 0.6, 40, P.red, { left: shade(P.red, -25) });
        box(ctx, gx + 0.25, gy + 0.28, 0.5, 0.3, 28, "#06122E");
        box(ctx, gx + 0.3, gy + 0.35, 0.4, 0.15, 18, P.amber);
        break;
      }
      case "laptop": {
        box(ctx, gx + 0.2, gy + 0.25, 0.6, 0.5, 6, P.slate);
        box(ctx, gx + 0.25, gy + 0.2, 0.5, 0.15, 18, P.navy);
        break;
      }
      case "server": {
        box(ctx, gx + 0.2, gy + 0.2, 0.6, 0.6, 36, P.graphite, { left: P.navyDeep });
        box(ctx, gx + 0.28, gy + 0.28, 0.44, 0.15, 10, P.green);
        box(ctx, gx + 0.28, gy + 0.45, 0.44, 0.15, 10, P.cyan);
        break;
      }
      case "mat":
      case "rug": {
        box(ctx, gx + 0.05, gy + 0.05, 0.9, 0.9, 3, f.c || P.indigo, {
          left: shade(P.indigo, -20),
        });
        break;
      }
      case "statue": {
        box(ctx, gx + 0.25, gy + 0.25, 0.5, 0.5, 10, P.stone);
        box(ctx, gx + 0.32, gy + 0.32, 0.36, 0.36, 28, P.slate, { left: P.graphite });
        box(ctx, gx + 0.35, gy + 0.35, 0.3, 0.3, 40, P.cloud);
        break;
      }
      case "frame": {
        // Framed wall photo — face into the room from the back wall.
        // doorWall is the *door* side; art hangs on the opposite wall and faces the door.
        const img = f.fileId ? wallArtImages.get(f.fileId) : null;
        const dw = f.doorWall || "S";
        // E/W pods use SW-facing frames; N/S pods use SE-facing; nudge for N (south wall art).
        const faceFn = dw === "E" || dw === "W" ? faceSW : faceSE;
        const yNudge = dw === "N" ? 0 : dw === "S" ? 1 : 0.5;
        faceFn(ctx, gx, gy + yNudge, 52, (c2) => {
          c2.fillStyle = "#3D2914";
          c2.fillRect(0, 0, 34, 42);
          c2.fillStyle = "#F5E6D3";
          c2.fillRect(3, 3, 28, 32);
          if (img && img.complete && img.naturalWidth > 0) {
            try {
              c2.drawImage(img, 4, 4, 26, 30);
            } catch {
              c2.fillStyle = P.sky;
              c2.fillRect(4, 4, 26, 30);
            }
          } else {
            c2.fillStyle = P.sky;
            c2.fillRect(4, 4, 26, 30);
            c2.fillStyle = "rgba(0,24,71,0.35)";
            c2.font = "bold 7px sans-serif";
            c2.fillText("PHOTO", 7, 22);
          }
          c2.fillStyle = "#C9A227";
          c2.fillRect(2, 2, 30, 2);
          c2.fillRect(2, 36, 30, 2);
        });
        break;
      }
    }
  }

  /** Dynamic decor furniture entries (rebuilt from personalOfficeState). */
  let decorFurn = [];
  /** Cache of wall-art images: fileId -> HTMLImageElement | null (failed) */
  const wallArtImages = new Map();
  const wallArtLoading = new Set();

  function ensureWallArtImage(fileId, placeholder) {
    if (!fileId || wallArtImages.has(fileId) || wallArtLoading.has(fileId)) return;
    wallArtLoading.add(fileId);
    // Prefer placeholder immediately for paint
    if (placeholder && typeof placeholder === "string" && placeholder.startsWith("data:")) {
      const img = new Image();
      img.src = placeholder;
      wallArtImages.set(fileId, img);
    }
    // Fetch full image (any authed HQ peer may resolve wall-art file ids)
    fetch(`/api/files/${fileId}`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        wallArtLoading.delete(fileId);
        const url = body?.data?.url || body?.data?.thumbnailUrl;
        if (!url) return;
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          wallArtImages.set(fileId, img);
        };
        img.onerror = () => {
          /* keep placeholder if any */
        };
        img.src = url;
      })
      .catch(() => {
        wallArtLoading.delete(fileId);
      });
  }

  function rebuildDecorFurn() {
    // Remove prior decor markers from walk grid is not tracked; only re-push furn list slice.
    decorFurn = [];
    for (const slot of officeSlots) {
      const st = personalOfficeState.get(slot.slotKey);
      if (!st) continue;
      if (Array.isArray(st.decorIds)) {
        st.decorIds.forEach((id, i) => {
          const pos = slot.decorSlots[i] || slot.decorSlots[0];
          if (!pos) return;
          const kind = isMapFurnitureKind(id) ? id : "plant";
          decorFurn.push({ kind, gx: pos.gx, gy: pos.gy, w: 1, d: 1, dir: "S", decor: true });
        });
      }
      // Wall photo on dedicated back-wall tile (never desk/seat).
      if (st.wallArtFileId) {
        ensureWallArtImage(st.wallArtFileId, st.wallArtPlaceholder);
        const pos = slot.wallArt || slot.decorSlots[0];
        if (pos) {
          decorFurn.push({
            kind: "frame",
            gx: pos.gx,
            gy: pos.gy,
            w: 1,
            d: 1,
            dir: "S",
            decor: true,
            fileId: st.wallArtFileId,
            doorWall: slot.doorWall,
            slotKey: slot.slotKey,
            // Draw before desk/chair on same depth band so the photo isn't buried
            depthBias: -20,
          });
        }
      }
    }
    warmFurnitureSprites();
  }

  function mayEnterPersonalOfficeTile(playerId, gx, gy) {
    const z = zoneAt(gx, gy);
    if (!z.personal) return true;
    const st = personalOfficeState.get(z.id);
    const ownerId = st?.ownerUserId ?? null;
    const doorStatus = st?.doorStatus || "closed";
    if (ownerId && ownerId === playerId) return true;
    if (!ownerId) return false;
    return doorStatus === "open";
  }

  // ---- main element ----
  class VyWorld extends HTMLElement {
    connectedCallback() {
      if (this.inited) {
        // reconnected after a React remount: restart loop + listeners
        window.removeEventListener("keydown", this.keyH);
        window.addEventListener("keydown", this.keyH);
        cancelAnimationFrame(this.raf);
        this.lastT = performance.now();
        this.raf = requestAnimationFrame(() => this.loop());
        return;
      }
      this.inited = true;
      this.style.cssText =
        "display:block;width:100%;height:100%;position:relative;overflow:hidden;background:#DDE4EE;";
      this.injectCSS();
      this.cv = document.createElement("canvas");
      this.cv.style.cssText = "position:absolute;inset:0;display:block;cursor:pointer;";
      this.ov = document.createElement("div");
      this.ov.style.cssText = "position:absolute;inset:0;overflow:hidden;pointer-events:none;";
      this.appendChild(this.cv);
      this.appendChild(this.ov);
      this.cfg = { radius: 7, showNames: true };
      this.cam = { x: 0, y: 0 };
      // Room focus auto-zoom × userZoom (scroll / +/-) for wide-map navigation.
      this.view = {
        scale: 1,
        targetScale: 1,
        autoTarget: 1,
        userZoom: 1,
        minUserZoom: 0.38,
        maxUserZoom: 2.4,
      };
      this.time = 0;
      this.lastT = performance.now();
      this.nearAt = 0;
      this.meeting = {
        active: false,
        live: false,
        presenter: "",
        topic: "Live huddle",
        wasInside: false,
        roomId: null,
        sitting: false,
      };
      this._lastSitKey = null;
      this._lastMeetingSig = null;
      this.initEntities();
      this.buildFloor();
      this.initMinimap();
      this.initZoomChrome();
      warmFurnitureSprites();
      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this);
      this.resize();
      this.cv.addEventListener("pointerdown", (e) => this.onClick(e));
      this.wheelH = (e) => this.onWheel(e);
      this.cv.addEventListener("wheel", this.wheelH, { passive: false });
      this.keyH = (e) => this.onKey(e);
      window.addEventListener("keydown", this.keyH);
      this.raf = requestAnimationFrame(() => this.loop());
      this.dispatchEvent(new CustomEvent("vy-ready"));
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      window.removeEventListener("keydown", this.keyH);
      if (this.cv && this.wheelH) this.cv.removeEventListener("wheel", this.wheelH);
      if (this.ro) this.ro.disconnect();
    }
    injectCSS() {
      if (document.getElementById("vy-style")) return;
      // style element created below — minimap rules appended with the rest
      const s = document.createElement("style");
      s.id = "vy-style";
      s.textContent = `
@keyframes vy-pop{0%{transform:translate(-50%,-100%) scale(.6);opacity:0}100%{transform:translate(-50%,-100%) scale(1);opacity:1}}
@keyframes vy-eq{0%,100%{height:3px}50%{height:9px}}
@keyframes vy-vid{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
.vy-ent{position:absolute;left:0;top:0;pointer-events:none;}
.vy-lab{position:absolute;left:0;top:0;transform-origin:50% 100%;transform:translate(-50%,-64px);white-space:nowrap;display:flex;align-items:center;gap:5px;background:rgba(0,24,71,0.78);color:#fff;font:600 10.5px Raleway,sans-serif;padding:3px 8px;border-radius:999px;letter-spacing:.02em;}
.vy-dot{width:6px;height:6px;border-radius:50%;flex:none;}
.vy-bub{position:absolute;left:0;top:0;transform-origin:50% 100%;transform:translate(-50%,-64px);animation:vy-pop .18s cubic-bezier(0.22,1,0.36,1);background:#fff;border:1px solid #E6EBF2;box-shadow:0 4px 12px rgba(0,24,71,.14);color:#1F2A44;font:500 11.5px Raleway,sans-serif;padding:6px 10px;border-radius:10px;max-width:190px;white-space:normal;line-height:1.35;}
.vy-bub:after{content:'';position:absolute;left:50%;bottom:-5px;width:8px;height:8px;background:#fff;border-right:1px solid #E6EBF2;border-bottom:1px solid #E6EBF2;transform:translateX(-50%) rotate(45deg);}
.vy-cam{position:absolute;left:0;top:0;transform-origin:50% 100%;transform:translate(-50%,-64px);width:96px;border-radius:10px;overflow:hidden;box-shadow:0 6px 16px rgba(0,24,71,.24);border:2px solid #2EA86A;background:#001847;animation:vy-pop .2s cubic-bezier(0.22,1,0.36,1);}
.vy-vid{height:56px;display:flex;align-items:center;justify-content:center;color:#fff;font:700 17px Montserrat,sans-serif;background-size:300% 300%;animation:vy-vid 7s ease infinite;position:relative;}
.vy-scan{position:absolute;inset:0;background:repeating-linear-gradient(0deg,rgba(255,255,255,0.05) 0 1px,transparent 1px 3px);}
.vy-camf{display:flex;align-items:center;justify-content:space-between;padding:2px 6px;background:#001847;color:#fff;font:600 8.5px Raleway,sans-serif;letter-spacing:.03em;}
.vy-eq{display:flex;align-items:flex-end;gap:1.5px;height:9px;}
.vy-eq i{width:2.5px;background:#2D9BD6;border-radius:1px;animation:vy-eq .7s ease-in-out infinite;}
.vy-eq i:nth-child(2){animation-delay:.15s}.vy-eq i:nth-child(3){animation-delay:.3s}
.vy-mut{width:7px;height:7px;border-radius:50%;background:#D8443C;}
.vy-emote{position:absolute;left:0;top:0;transform-origin:50% 100%;transform:translate(-50%,-64px);font:700 12px Montserrat,sans-serif;color:#001847;background:#FFD65A;border:1.5px solid #001847;border-radius:999px;padding:2px 7px;animation:vy-pop .18s ease-out;}
.vy-minimap{position:absolute;top:12px;right:12px;z-index:30;border-radius:12px;overflow:hidden;box-shadow:0 8px 24px rgba(0,24,71,.35);border:1px solid rgba(45,155,214,.45);pointer-events:auto;}
.vy-minimap-label{position:absolute;top:6px;left:8px;font:700 8px Montserrat,sans-serif;letter-spacing:.14em;color:rgba(255,255,255,.55);pointer-events:none;}
.vy-zoom{position:absolute;top:154px;right:12px;z-index:30;display:flex;flex-direction:column;gap:4px;pointer-events:auto;}
.vy-zoom button{width:34px;height:34px;border-radius:10px;border:1px solid rgba(45,155,214,.45);background:rgba(0,15,46,.88);color:#fff;font:700 16px Montserrat,sans-serif;cursor:pointer;line-height:1;box-shadow:0 4px 12px rgba(0,24,71,.3);}
.vy-zoom button:hover{background:rgba(0,24,71,.95);border-color:#2D9BD6;}
.vy-zoom-lbl{text-align:center;font:700 9px Montserrat,sans-serif;letter-spacing:.06em;color:rgba(255,255,255,.7);padding:2px 0;}
`;
      document.head.appendChild(s);
    }
    initEntities() {
      // Multiplayer only — remote players arrive via upsertPeer(); no NPC bots.
      this.remotes = new Map(); // id -> entity
      this.player = {
        id: "you",
        name: "You",
        role: "Nymbl",
        isRemote: false,
        colors: {
          shirt: "#1E6CC7",
          hair: "#2B2118",
          skin: "#E9BE93",
          style: "short",
          pants: "#1F2A44",
          shoes: "#001847",
          hat: "none",
          hatColor: "#1E6CC7",
          glasses: "none",
          facialHair: "none",
          jewelry: "none",
        },
        gx: 26,
        gy: 20,
        fx: 26,
        fy: 20,
        path: [],
        step: null,
        facing: "SE",
        frame: 0,
        ft: 0,
        sitting: null,
        status: "available",
        baseStatus: "available",
        camOn: true,
        micOn: true,
        talking: false,
        bubble: null,
        emote: null,
        action: null, // { id, start, until, emoji }
        zone: "commons",
      };
      this.entities = [this.player];
      this.dom = {};
      this.entities.forEach((e) => {
        // always register local player under "you" for configure()
        const w = document.createElement("div");
        w.className = "vy-ent";
        const lab = document.createElement("div");
        lab.className = "vy-lab";
        lab.innerHTML = `<span class="vy-dot"></span><span></span>`;
        const bub = document.createElement("div");
        bub.className = "vy-bub";
        bub.style.display = "none";
        const cam = document.createElement("div");
        cam.className = "vy-cam";
        cam.style.display = "none";
        const ini = e.name
          .split(" ")
          .map((x) => x[0])
          .join("")
          .slice(0, 2)
          .toUpperCase();
        const c1 = e.colors.shirt;
        cam.innerHTML = `<div class="vy-vid" style="background-image:linear-gradient(120deg, ${shade(c1, -60)}, ${c1}, ${shade(c1, 40)}, ${shade(c1, -30)})"><span></span><i class="vy-scan"></i><span style="position:relative">${ini}</span></div><div class="vy-camf"><span class="vy-camname">You</span><span class="vy-ind"></span></div>`;
        const emo = document.createElement("div");
        emo.className = "vy-emote";
        emo.style.display = "none";
        w.appendChild(lab);
        w.appendChild(bub);
        w.appendChild(cam);
        w.appendChild(emo);
        this.ov.appendChild(w);
        this.dom[e.id] = {
          w,
          lab,
          bub,
          cam,
          emo,
          labName: lab.children[1],
          labDot: lab.children[0],
          ind: cam.querySelector(".vy-ind"),
        };
      });
    }
    buildFloor() {
      // Supersample the baked floor to device resolution so it stays crisp under the
      // zoom transform instead of nearest-neighbor upscaling a half-res bitmap.
      const FS = this.floorScale || 2;
      const cnv = document.createElement("canvas");
      cnv.width = Math.ceil(WORLD_W * FS);
      cnv.height = Math.ceil((WORLD_H + MTOP + MBOT) * FS);
      const ctx = cnv.getContext("2d");
      ctx.setTransform(FS, 0, 0, FS, 0, 0);
      // Re-bake once when an async ground-texture load completes (cold-cache race:
      // the first bake would otherwise be stuck on flat fallback fills).
      const markFloorDirty = () => {
        this.floorDirty = true;
      };
      const isPlaza =
        currentLayout &&
        (currentLayout.id === "plaza" ||
          currentLayout.id === "campus" ||
          currentLayout.theme === "outdoor");
      // Warm outdoor ground textures so first paint can use sprites
      if (isPlaza) {
        for (const id of [
          "grass",
          "cobble",
          "cobble_round",
          "cobble_sand",
          "brick",
          "brick_dark",
          "sidewalk",
          "pavement",
          "dirt",
          "wood_deck",
        ]) {
          const tile = FLOOR_TILES[id];
          if (tile?.src) ensureSprite(tile.src, markFloorDirty);
        }
      }
      // Outer shell — mist office walls vs greener outdoor perimeter
      const shell = isPlaza ? "#8FBF7A" : P.mist;
      const shellDeep = isPlaza ? shade("#8FBF7A", -18) : shade(P.mist, -14);
      const shellTop = isPlaza ? shade("#8FBF7A", -28) : shade(P.mist, -18);
      for (let x = 1; x < COLS; x++) {
        box(ctx, x, 0, 1, 1, 84, shell, { left: shellDeep, right: shell, outline: false });
        if (!isPlaza && x % 3 === 1 && x < COLS - 1)
          faceSW(ctx, x, 1, 74, (c2) => {
            const g = c2.createLinearGradient(0, 0, 0, 40);
            g.addColorStop(0, "#BFE0F5");
            g.addColorStop(1, "#E8F2FB");
            c2.fillStyle = g;
            c2.fillRect(5, 12, 24, 42);
            c2.fillStyle = P.stone;
            c2.fillRect(5, 32, 24, 2);
            c2.strokeStyle = P.stone;
            c2.strokeRect(5, 12, 24, 42);
          });
      }
      for (let y = 0; y < ROWS; y++) {
        const A = iso(0, y),
          B = iso(1, y),
          C = iso(1, y + 1),
          D = iso(0, y + 1),
          h = 84;
        poly(
          ctx,
          [
            [C.x, C.y],
            [B.x, B.y],
            [B.x, B.y - h],
            [C.x, C.y - h],
          ],
          shade(shell, -8),
        );
        poly(
          ctx,
          [
            [C.x, C.y],
            [B.x, B.y],
            [B.x, B.y - 10],
            [C.x, C.y - 10],
          ],
          isPlaza ? shade("#4A7A3A", -10) : P.navy,
        );
        poly(
          ctx,
          [
            [A.x, A.y - h],
            [B.x, B.y - h],
            [C.x, C.y - h],
            [D.x, D.y - h],
          ],
          shellTop,
        );
      }
      // Floor tiles — HQ cool checker + zone rugs; outdoor Habbo ground textures
      // Warm yellow-green lawn so dark tree canopies pop (avoid same mid-green as foliage).
      const floorFallback = {
        grass: "#C6D96A",
        cobble: "#B8BCC6",
        cobble_round: "#9CA3B0",
        cobble_sand: "#C4B8A0",
        brick: "#B85A42",
        brick_dark: "#6B3A2E",
        sidewalk: "#D0D4DC",
        pavement: "#A8ACB8",
        dirt: "#8B6F47",
        wood_deck: "#C4A574",
      };
      for (let gx = 1; gx < COLS; gx++)
        for (let gy = 1; gy < ROWS; gy++) {
          const z = zoneAt(gx, gy),
            even = (gx + gy) % 2 === 0;
          let c;
          let floorId = null;
          if (isPlaza) {
            // Default lawn: mild yellow-green variation (strong checker showed through foliage).
            c = even ? "#C2D46E" : "#B8CA62";
            floorId = "grass";
            if (z.rug) {
              c = even ? z.rug : shade(z.rug, -10);
              const rugKey = String(z.rug).toUpperCase();
              floorId =
                OUTDOOR_FLOOR_BY_RUG[z.rug] ||
                OUTDOOR_FLOOR_BY_RUG[rugKey] ||
                Object.entries(OUTDOOR_FLOOR_BY_RUG).find(
                  ([k]) => k.toUpperCase() === rugKey,
                )?.[1] ||
                null;
              if (!floorId) {
                // Soft cobble checker on stone-ish hexes
                if (z.rug === "#B8BCC6" || z.rug === "#A8ACB8") {
                  c = even ? shade(z.rug, 8) : shade(z.rug, -6);
                  floorId = z.rug === "#A8ACB8" ? "pavement" : "cobble";
                }
              }
            }
          } else {
            // Cool office tile — deeper than the old near-white so interiors read as a
            // finished tiled floor, not an empty void. Grout lines added below.
            c = even ? "#D7E1F1" : "#C4D2E6";
            // North corridor south of offices (y=6–7) — slightly warmer wood-ish path
            if (gy >= 6 && gy <= 7 && gx >= 2 && gx <= 51) {
              c = even ? "#E4DBCB" : "#D6CBB8";
            }
            if (z.rug) c = even ? z.rug : shade(z.rug, -7);
          }
          const A = iso(gx, gy),
            B = iso(gx + 1, gy),
            C = iso(gx + 1, gy + 1),
            D = iso(gx, gy + 1);
          let drewTile = false;
          if (isPlaza && floorId && FLOOR_TILES[floorId]) {
            const tile = FLOOR_TILES[floorId];
            const img = ensureSprite(tile.src);
            if (img && img.naturalWidth) {
              // Base fill under textured diamond (avoids gaps)
              poly(
                ctx,
                [
                  [A.x, A.y],
                  [B.x, B.y],
                  [C.x, C.y],
                  [D.x, D.y],
                ],
                floorFallback[floorId] || c,
              );
              ctx.save();
              ctx.beginPath();
              ctx.moveTo(A.x, A.y);
              ctx.lineTo(B.x, B.y);
              ctx.lineTo(C.x, C.y);
              ctx.lineTo(D.x, D.y);
              ctx.closePath();
              ctx.clip();
              // Iso diamond image is TW×TH, top vertex at A
              ctx.imageSmoothingEnabled = false;
              ctx.drawImage(img, A.x - TW / 2, A.y, TW, TH);
              // Very mild checker — strong seams read as "squares inside bushes"
              if (even) {
                ctx.fillStyle = "rgba(255,255,255,0.02)";
                ctx.fill();
              } else {
                ctx.fillStyle = "rgba(0,0,0,0.025)";
                ctx.fill();
              }
              ctx.restore();
              drewTile = true;
            }
          }
          if (!drewTile) {
            poly(
              ctx,
              [
                [A.x, A.y],
                [B.x, B.y],
                [C.x, C.y],
                [D.x, D.y],
              ],
              c,
            );
            // Indoor grout — thin diamond outline gives flat office tiles definition.
            if (!isPlaza && !z.rug) {
              ctx.strokeStyle = shade(c, -12);
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(A.x, A.y);
              ctx.lineTo(B.x, B.y);
              ctx.lineTo(C.x, C.y);
              ctx.lineTo(D.x, D.y);
              ctx.closePath();
              ctx.stroke();
            }
          }
        }
      // Transition pads — HQ: bright mats. Campus: soft warm wash (village-ref uses door
      // glow, not hard yellow plates under every entrance).
      const transitions = (currentLayout && currentLayout.transitions) || [];
      for (const t of transitions) {
        if (typeof t.gx !== "number" || typeof t.gy !== "number") continue;
        const A = iso(t.gx, t.gy),
          B = iso(t.gx + 1, t.gy),
          C = iso(t.gx + 1, t.gy + 1),
          D = iso(t.gx, t.gy + 1);
        if (isPlaza) {
          poly(
            ctx,
            [
              [A.x, A.y],
              [B.x, B.y],
              [C.x, C.y],
              [D.x, D.y],
            ],
            "rgba(255, 220, 140, 0.28)",
          );
        } else {
          poly(
            ctx,
            [
              [A.x, A.y],
              [B.x, B.y],
              [C.x, C.y],
              [D.x, D.y],
            ],
            "#5BB8E8",
          );
          poly(
            ctx,
            [
              [(A.x + D.x) / 2, (A.y + D.y) / 2 - 2],
              [(B.x + A.x) / 2, (B.y + A.y) / 2 - 2],
              [(C.x + B.x) / 2, (C.y + B.y) / 2 - 2],
              [(D.x + C.x) / 2, (D.y + C.y) / 2 - 2],
            ],
            "#A8DCF5",
          );
        }
      }
      // Zone decals — HQ only (campus labels clutter the village street)
      const decal = (gx, gy, txt) => {
        const p0 = iso(gx, gy);
        ctx.save();
        ctx.translate(p0.x, p0.y);
        ctx.transform(1, 0.5, -1, 0.5, 0, 0);
        ctx.fillStyle = "rgba(0,24,71,0.20)";
        ctx.font = "700 13px Montserrat, sans-serif";
        ctx.letterSpacing = "3px";
        ctx.fillText(txt, 0, 0);
        ctx.restore();
      };
      if (!isPlaza) {
        for (const z of commonZones) {
          const mx = Math.floor((z.x1 + z.x2) / 2);
          const my = Math.floor((z.y1 + z.y2) / 2);
          decal(mx, my, String(z.name || z.id).toUpperCase());
        }
        for (const t of transitions) {
          if (t.label) decal(t.gx, t.gy - 0.4, String(t.label).toUpperCase());
        }
      }
      this.floor = cnv;
      // Vignette is now drawn live in screen space each frame (see render) — a scaled
      // 512² bitmap banded under nearest-neighbor upscaling.
    }
    resize() {
      this.vw = Math.max(50, this.clientWidth);
      this.vh = Math.max(50, this.clientHeight);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.dpr = dpr;
      this.cv.width = this.vw * dpr;
      this.cv.height = this.vh * dpr;
      this.cv.style.width = this.vw + "px";
      this.cv.style.height = this.vh + "px";
    }
    // ---- public API ----
    configure(o) {
      if (o.name) this.player.name = o.name;
      if (o.colors) Object.assign(this.player.colors, o.colors);
      if (o.radius != null) this.cfg.radius = o.radius;
      if (o.showNames != null) this.cfg.showNames = !!o.showNames;
      if (o.playerId) {
        const prev = this.player.id;
        this.player.id = o.playerId;
        if (this.dom.you && prev === "you") {
          this.dom[o.playerId] = this.dom.you;
        }
      }
      if (o.gx != null && o.gy != null) {
        const p = this.player;
        p.path = [];
        p.step = null;
        p.sitting = null;
        p.gx = o.gx;
        p.gy = o.gy;
        p.fx = o.gx;
        p.fy = o.gy;
      }
      const c1 = this.player.colors.shirt,
        cam = this.dom.you && this.dom.you.cam;
      if (cam) {
        const vid = cam.querySelector(".vy-vid");
        if (vid && !vid.querySelector("video")) {
          vid.style.backgroundImage = `linear-gradient(120deg, ${shade(c1, -60)}, ${c1}, ${shade(c1, 40)}, ${shade(c1, -30)})`;
        }
      }
      this.refreshPlayerDom();
    }
    setStatus(s) {
      this.player.baseStatus = s;
      this.player.status = s;
    }
    setMic(v) {
      this.player.micOn = !!v;
    }
    setCam(v) {
      this.player.camOn = !!v;
    }
    say(text) {
      this.bubble(this.player, text, 3800);
      this.player.talking = true;
      setTimeout(() => (this.player.talking = false), 2000);
    }
    /** @deprecated Prefer playAction("wave") */
    wave() {
      this.playAction("wave");
    }
    /**
     * Play a local avatar action (body pose + emoji pill).
     * Returns false if still cooling down.
     */
    playAction(actionId, opts) {
      opts = opts || {};
      const meta = getOfficeAction(actionId);
      if (!meta) return false;
      const p = this.player;
      if (!opts.force && p.action && p.action.until > this.time + 0.05) {
        // Allow interrupt only after most of current action, or force.
        if (p.action.until - this.time > 0.25) return false;
      }
      this.applyEntityAction(p, actionId, { silent: !!opts.silent });
      // Face nearest peer for handshake
      if (actionId === "handshake") {
        const near = this.nearestRemote(0.2);
        if (near) this.faceToward(p, near);
      }
      if (!opts.silent && meta.social) {
        const near = this.remoteList().filter((b) => this.volumeOf(b) > 0.05);
        if (near.length && actionId === "wave") {
          this.emit("vy-wave-ack", {
            count: near.length,
            names: near.map((b) => b.name.split(" ")[0]),
          });
        }
      }
      return true;
    }
    /** Remote peer played an action */
    peerAction(id, actionId) {
      const e = this.remotes.get(id);
      if (!e) return;
      this.applyEntityAction(e, actionId, { silent: true });
      // Reciprocal handshake when they greet us from nearby
      if (actionId === "handshake" && this.volumeOf(e) > 0.12) {
        this.faceToward(this.player, e);
        this.faceToward(e, this.player);
        if (!this.player.action || this.player.action.until < this.time) {
          this.applyEntityAction(this.player, "handshake", { silent: true });
        }
      }
    }
    /** @deprecated Prefer peerAction(id, "wave") */
    peerWave(id) {
      this.peerAction(id, "wave");
    }
    applyEntityAction(e, actionId, opts) {
      opts = opts || {};
      const meta = getOfficeAction(actionId);
      if (!meta || !e) return;
      const duration = meta.duration;
      e.action = {
        id: actionId,
        start: this.time,
        until: this.time + duration,
        emoji: meta.emoji,
      };
      e.emote = { until: this.time + duration, txt: meta.emoji };
    }
    nearestRemote(minVol) {
      let best = null,
        bestV = minVol || 0;
      for (const b of this.remoteList()) {
        const v = this.volumeOf(b);
        if (v > bestV) {
          bestV = v;
          best = b;
        }
      }
      return best;
    }
    faceToward(from, to) {
      if (!from || !to) return;
      const dx = to.fx - from.fx,
        dy = to.fy - from.fy;
      if (Math.abs(dx) + Math.abs(dy) < 0.01) return;
      // Iso facing quadrants
      if (dx >= 0 && dy >= 0) from.facing = "SE";
      else if (dx < 0 && dy >= 0) from.facing = "SW";
      else if (dx < 0 && dy < 0) from.facing = "NW";
      else from.facing = "NE";
    }
    walkToPerson(id) {
      const b = this.remotes.get(id);
      if (!b) return;
      const opts = [
        [b.gx + 1, b.gy],
        [b.gx, b.gy + 1],
        [b.gx - 1, b.gy],
        [b.gx, b.gy - 1],
      ].filter(([x, y]) => isWalk(x, y));
      if (opts.length) this.routePlayer(opts[0][0], opts[0][1]);
    }
    /**
     * Path to a personal office by slotKey.
     * Open/ownable: walk to the door (or seat if allowed). Closed: stop outside the door.
     */
    walkToOffice(slotKey) {
      if (!slotKey) return;
      const slot = officeSlots.find((s) => s.slotKey === slotKey);
      if (!slot) {
        this.emit("vy-toast", { text: "Office not found on the map." });
        return;
      }
      let tx = slot.door.gx;
      let ty = slot.door.gy;
      const canEnterDoor = mayEnterPersonalOfficeTile(this.player.id, tx, ty);
      if (!canEnterDoor) {
        // Corridor tile just outside the door
        if (slot.doorWall === "S") ty = slot.door.gy + 1;
        else if (slot.doorWall === "N") ty = slot.door.gy - 1;
        else if (slot.doorWall === "E") tx = slot.door.gx + 1;
        else if (slot.doorWall === "W") tx = slot.door.gx - 1;
      } else if (
        slot.interiorSeat &&
        mayEnterPersonalOfficeTile(this.player.id, slot.interiorSeat.gx, slot.interiorSeat.gy)
      ) {
        // Prefer the desk seat when the office is enterable
        tx = slot.interiorSeat.gx;
        ty = slot.interiorSeat.gy;
      }
      if (!isWalk(tx, ty) && !seats[`${tx},${ty}`]) {
        // Try adjacent walkable tiles around the door
        const candidates = [
          [slot.door.gx, slot.door.gy],
          [slot.door.gx + 1, slot.door.gy],
          [slot.door.gx - 1, slot.door.gy],
          [slot.door.gx, slot.door.gy + 1],
          [slot.door.gx, slot.door.gy - 1],
        ];
        const hit = candidates.find(
          ([x, y]) =>
            (isWalk(x, y) || seats[`${x},${y}`]) &&
            mayEnterPersonalOfficeTile(this.player.id, x, y),
        );
        const anyWalk = candidates.find(([x, y]) => isWalk(x, y) || seats[`${x},${y}`]);
        const pick = hit || anyWalk;
        if (!pick) {
          this.emit("vy-toast", { text: "Can't find a path to that office." });
          return;
        }
        tx = pick[0];
        ty = pick[1];
      }
      this.routePlayer(tx, ty);
    }
    /** Manual zoom multiplier on top of room auto-focus (1 = default). */
    setUserZoom(z) {
      const lo = this.view.minUserZoom || 0.38;
      const hi = this.view.maxUserZoom || 2.4;
      this.view.userZoom = clamp(Number(z) || 1, lo, hi);
      this.syncZoomChrome();
    }
    zoomBy(factor) {
      this.setUserZoom((this.view.userZoom || 1) * factor);
    }
    zoomIn() {
      this.zoomBy(1.14);
    }
    zoomOut() {
      this.zoomBy(1 / 1.14);
    }
    resetZoom() {
      this.setUserZoom(1);
    }
    getUserZoom() {
      return this.view.userZoom || 1;
    }
    onWheel(e) {
      // Pinch / trackpad / mouse wheel → zoom map (easier long-range click-to-walk)
      if (e.ctrlKey || Math.abs(e.deltaY) > 0) {
        e.preventDefault();
        const dir = e.deltaY > 0 ? 1 / 1.08 : 1.08;
        // Larger steps for mouse wheel notches
        const step = Math.abs(e.deltaY) >= 40 ? (e.deltaY > 0 ? 1 / 1.14 : 1.14) : dir;
        this.zoomBy(step);
      }
    }
    initZoomChrome() {
      // React owns the zoom controls when the host opts in (see VyllageWorldHost /
      // SceneControls); skip the engine-injected buttons so there's one chrome system.
      if (this.dataset.reactZoom === "1") return;
      const wrap = document.createElement("div");
      wrap.className = "vy-zoom";
      wrap.setAttribute("aria-label", "Map zoom");
      const mk = (label, title, fn) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.title = title;
        b.addEventListener("pointerdown", (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          fn();
        });
        return b;
      };
      wrap.appendChild(mk("+", "Zoom in (− key)", () => this.zoomIn()));
      const lbl = document.createElement("div");
      lbl.className = "vy-zoom-lbl";
      lbl.textContent = "100%";
      wrap.appendChild(lbl);
      wrap.appendChild(mk("−", "Zoom out (+ key)", () => this.zoomOut()));
      wrap.appendChild(mk("⟲", "Reset zoom (0)", () => this.resetZoom()));
      this.appendChild(wrap);
      this.zoomChrome = { wrap, lbl };
      this.syncZoomChrome();
    }
    syncZoomChrome() {
      if (!this.zoomChrome?.lbl) return;
      const pct = Math.round((this.view.userZoom || 1) * 100);
      this.zoomChrome.lbl.textContent = `${pct}%`;
    }
    /** Snapshot of local player for network sync */
    getPose() {
      const p = this.player;
      return {
        gx: p.gx,
        gy: p.gy,
        fx: p.fx,
        fy: p.fy,
        facing: p.facing,
        status: p.status,
        mic: !!p.micOn,
        cam: !!p.camOn,
        zoneId: p.zone,
      };
    }
    volumeOfId(id) {
      const e = this.remotes.get(id);
      return e ? this.volumeOf(e) : 0;
    }
    /** Upsert a remote multiplayer avatar */
    upsertPeer(snap) {
      if (!this.remotes) this.remotes = new Map();
      let e = this.remotes.get(snap.id);
      if (!e) {
        e = {
          id: snap.id,
          name: snap.name,
          role: snap.role || "Teammate",
          isRemote: true,
          colors: Object.assign({}, snap.colors),
          gx: snap.gx,
          gy: snap.gy,
          fx: snap.fx != null ? snap.fx : snap.gx,
          fy: snap.fy != null ? snap.fy : snap.gy,
          path: [],
          step: null,
          facing: snap.facing || "SE",
          frame: 0,
          ft: 0,
          sitting: null,
          status: snap.status || "available",
          camOn: snap.cam !== false,
          micOn: snap.mic !== false,
          sharing: snap.sharing === true,
          talking: false,
          bubble: null,
          emote: null,
          action: null,
          zone: snap.zoneId || "commons",
          stream: null,
        };
        e.targetFx = e.fx;
        e.targetFy = e.fy;
        this.remotes.set(snap.id, e);
        this.entities = [this.player, ...this.remoteList()];
        this.ensureEntityDom(e);
      } else {
        e.name = snap.name || e.name;
        e.role = snap.role || e.role;
        if (snap.colors) Object.assign(e.colors, snap.colors);
        // Smooth remote motion toward network pose
        e.targetFx = snap.fx != null ? snap.fx : snap.gx;
        e.targetFy = snap.fy != null ? snap.fy : snap.gy;
        e.gx = Math.round(e.targetFx);
        e.gy = Math.round(e.targetFy);
        if (snap.facing) e.facing = snap.facing;
        if (snap.status) e.status = snap.status;
        if (snap.cam != null) e.camOn = !!snap.cam;
        if (snap.mic != null) e.micOn = !!snap.mic;
        if (snap.sharing != null) e.sharing = !!snap.sharing;
        if (snap.zoneId) e.zone = snap.zoneId;
        this.refreshEntityLabel(e);
      }
    }
    removePeer(id) {
      const e = this.remotes.get(id);
      if (!e) return;
      // Detach media + drop addtrack listeners before DOM teardown (self-contained).
      const d = this.dom[id];
      if (d) this.attachStreamToCam(d, null, e);
      else this.clearCamAddTrackListener(e);
      e.stream = null;
      if (d && d.w) d.w.remove();
      delete this.dom[id];
      this.remotes.delete(id);
      this.entities = [this.player, ...this.remoteList()];
    }
    setPeerStream(id, stream) {
      const e = this.remotes.get(id);
      if (!e) return;
      e.stream = stream;
      const d = this.dom[id];
      if (!d) return;
      this.attachStreamToCam(d, stream, e);
    }
    setLocalStream(stream) {
      this.player.stream = stream;
      const d = this.dom.you;
      if (!d) return;
      this.attachStreamToCam(d, stream, this.player);
    }
    /** Clear entity-scoped addtrack listener used when video arrives after audio. */
    clearCamAddTrackListener(e) {
      if (!e || !e._camAddTrackHandler || !e._camAddTrackStream) return;
      e._camAddTrackStream.removeEventListener("addtrack", e._camAddTrackHandler);
      e._camAddTrackHandler = null;
      e._camAddTrackStream = null;
    }
    attachStreamToCam(d, stream, e) {
      if (!d || !d.cam) return;
      const vidBox = d.cam.querySelector(".vy-vid");
      if (!vidBox) return;

      // Drop any prior late-track listener before rebinding or clearing.
      this.clearCamAddTrackListener(e);

      if (!stream) {
        const video = vidBox.querySelector("video");
        if (video) {
          video.srcObject = null;
          video.remove();
        }
        // Restore placeholder chrome (scan / initials / gradient).
        const scan = vidBox.querySelector(".vy-scan");
        if (scan) scan.style.display = "";
        const ini = vidBox.querySelector("span[style*='relative']");
        if (ini) ini.style.display = "";
        const c1 = e && e.colors && e.colors.shirt;
        if (c1) {
          vidBox.style.backgroundImage = `linear-gradient(120deg, ${shade(c1, -60)}, ${c1}, ${shade(c1, 40)}, ${shade(c1, -30)})`;
        }
        return;
      }

      const bindVideoIfReady = () => {
        if (!stream.getVideoTracks().length) return false;
        let video = vidBox.querySelector("video");
        if (!video) {
          video = document.createElement("video");
          video.autoplay = true;
          video.playsInline = true;
          video.muted = true;
          video.style.cssText =
            "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;";
          vidBox.appendChild(video);
        }
        if (video.srcObject !== stream) {
          video.srcObject = stream;
        }
        void video.play().catch(() => {});
        const scan = vidBox.querySelector(".vy-scan");
        if (scan) scan.style.display = "none";
        const ini = vidBox.querySelector("span[style*='relative']");
        if (ini) ini.style.display = "none";
        vidBox.style.backgroundImage = "none";
        return true;
      };

      if (bindVideoIfReady()) return;

      // Audio-first ontrack: stream exists but video track may land later.
      const onAdd = (ev) => {
        if (ev.track && ev.track.kind === "video" && bindVideoIfReady()) {
          // Self-unsubscribe once video is bound (do not wait for another setPeerStream).
          this.clearCamAddTrackListener(e);
        }
      };
      stream.addEventListener("addtrack", onAdd);
      if (e) {
        e._camAddTrackHandler = onAdd;
        e._camAddTrackStream = stream;
      }
    }
    ensureEntityDom(e) {
      if (this.dom[e.id]) return;
      const w = document.createElement("div");
      w.className = "vy-ent";
      const lab = document.createElement("div");
      lab.className = "vy-lab";
      lab.innerHTML = `<span class="vy-dot"></span><span></span>`;
      const bub = document.createElement("div");
      bub.className = "vy-bub";
      bub.style.display = "none";
      const cam = document.createElement("div");
      cam.className = "vy-cam";
      cam.style.display = "none";
      const ini = e.name
        .split(" ")
        .map((x) => x[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
      const c1 = e.colors.shirt;
      const label = e.isRemote ? e.name.split(" ")[0] : "You";
      cam.innerHTML = `<div class="vy-vid" style="background-image:linear-gradient(120deg, ${shade(c1, -60)}, ${c1}, ${shade(c1, 40)}, ${shade(c1, -30)})"><span></span><i class="vy-scan"></i><span style="position:relative">${ini}</span></div><div class="vy-camf"><span class="vy-camname">${label}</span><span class="vy-ind"></span></div>`;
      const emo = document.createElement("div");
      emo.className = "vy-emote";
      emo.style.display = "none";
      w.appendChild(lab);
      w.appendChild(bub);
      w.appendChild(cam);
      w.appendChild(emo);
      this.ov.appendChild(w);
      this.dom[e.id] = {
        w,
        lab,
        bub,
        cam,
        emo,
        labName: lab.children[1],
        labDot: lab.children[0],
        ind: cam.querySelector(".vy-ind"),
      };
      // setPeerStream may have stored e.stream before DOM existed — re-attach now.
      if (e.stream) this.attachStreamToCam(this.dom[e.id], e.stream, e);
    }
    refreshPlayerDom() {
      const d = this.dom.you;
      if (!d) return;
      const nameEl = d.cam.querySelector(".vy-camname") || d.cam.querySelector(".vy-camf span");
      if (nameEl)
        nameEl.textContent = this.player.name === "You" ? "You" : this.player.name.split(" ")[0];
    }
    refreshEntityLabel(e) {
      const d = this.dom[e.id];
      if (!d) return;
      const nameEl = d.cam.querySelector(".vy-camname") || d.cam.querySelector(".vy-camf span");
      if (nameEl) nameEl.textContent = e.name.split(" ")[0];
    }
    // ---- internals ----
    emit(name, detail) {
      this.dispatchEvent(new CustomEvent(name, { detail }));
    }
    remoteList() {
      return this.remotes ? [...this.remotes.values()] : [];
    }
    bubble(e, text, dur) {
      e.bubble = { text, until: this.time + (dur || 3600) / 1000 };
    }
    /** Enclosed common room (company meeting or any huddle). */
    inMeetingRoom(e) {
      const z = zoneAt(Math.round(e.fx), Math.round(e.fy));
      return !!(z.priv && !z.personal);
    }
    /** Any hard-isolated zone: meeting, huddle, or personal office. */
    inPrivateZone(e) {
      return this.privateZoneKey(e) !== null;
    }
    privateZoneKey(e) {
      const z = zoneAt(Math.round(e.fx), Math.round(e.fy));
      // Company meeting, huddles, and personal offices — isolation by zone id.
      if (z.priv || z.personal) return z.id;
      return null;
    }
    volumeOf(b) {
      const p = this.player;
      const pz = zoneAt(Math.round(p.fx), Math.round(p.fy));
      const bz = zoneAt(Math.round(b.fx), Math.round(b.fy));
      return computeSpatialVolume({
        self: {
          fx: p.fx,
          fy: p.fy,
          zone: {
            id: pz.id,
            privateKey: pz.priv || pz.personal ? pz.id : null,
            isFocus: pz.id === "focus",
          },
        },
        peer: {
          fx: b.fx,
          fy: b.fy,
          zone: {
            id: bz.id,
            privateKey: bz.priv || bz.personal ? bz.id : null,
            isFocus: bz.id === "focus",
          },
        },
        radius: this.cfg.radius,
      });
    }
    applyMapLayout(layout) {
      // Clear transition latch so we can re-fire after a map change lands us off the tile.
      this._lastTransitionId = null;
      rebuildStaticMap(layout);
      rebuildDecorFurn();
      warmFurnitureSprites();
      if (this.inited) {
        this.buildFloor();
        // Nudge local player if their tile is no longer walkable
        const p = this.player;
        if (
          p &&
          !isWalk(Math.round(p.fx), Math.round(p.fy)) &&
          !seats[`${Math.round(p.fx)},${Math.round(p.fy)}`]
        ) {
          const sp = currentLayout.spawnPoints[0] || { gx: 26, gy: 20 };
          p.path = [];
          p.step = null;
          p.sitting = null;
          p.gx = sp.gx;
          p.gy = sp.gy;
          p.fx = sp.gx;
          p.fy = sp.gy;
        }
      }
      // Per-map default framing: the outdoor campus pulls back so the whole plaza +
      // the (tall) Nymbl HQ fit on screen; indoor maps use 1×. Only reset on an actual
      // map change so a user's manual zoom isn't clobbered by a layout refresh.
      const newMapId = layout?.id || currentLayout?.id;
      if (newMapId !== this._lastMapId) {
        this._lastMapId = newMapId;
        this.setUserZoom(newMapId === "campus" ? 0.56 : 1);
      }
      this.dispatchEvent(
        new CustomEvent("vy-map-layout", {
          detail: { version: layout?.version, mapId: layout?.id || currentLayout?.id },
        }),
      );
    }
    /** Teleport local player to a spawn (used after map transition). */
    teleportPlayer(gx, gy) {
      const p = this.player;
      if (!p) return;
      const tx = Math.round(gx);
      const ty = Math.round(gy);
      p.path = [];
      p.step = null;
      p.sitting = null;
      p.gx = tx;
      p.gy = ty;
      p.fx = tx;
      p.fy = ty;
      this._lastTransitionId = null;
    }
    setPersonalOffices(offices) {
      personalOfficeState.clear();
      const list = Array.isArray(offices) ? offices : [];
      for (const o of list) {
        if (!o || !o.slotKey) continue;
        personalOfficeState.set(o.slotKey, {
          displayName: o.displayName || o.slotKey,
          wallArtFileId: o.wallArtFileId || null,
          wallArtPlaceholder: o.wallArtPlaceholder || null,
          theme: o.theme || null,
          ownerUserId: o.ownerUserId ?? null,
          doorStatus: o.doorStatus === "open" ? "open" : "closed",
          decorIds: Array.isArray(o.decorIds) ? o.decorIds : [],
        });
      }
      rebuildDecorFurn();
      // If local player is stuck in a now-closed office, nudge them out to the door tile exterior.
      const p = this.player;
      const z = zoneAt(Math.round(p.fx), Math.round(p.fy));
      if (z.personal && !mayEnterPersonalOfficeTile(p.id, Math.round(p.fx), Math.round(p.fy))) {
        const slot = officeSlots.find((s) => s.slotKey === z.id);
        if (slot) {
          p.path = [];
          p.step = null;
          p.sitting = null;
          // Step one tile outside the door into the corridor
          let ox = slot.door.gx,
            oy = slot.door.gy;
          if (slot.doorWall === "S") oy += 1;
          else if (slot.doorWall === "N") oy -= 1;
          else if (slot.doorWall === "E") ox += 1;
          else if (slot.doorWall === "W") ox -= 1;
          p.gx = ox;
          p.gy = oy;
          p.fx = p.gx;
          p.fy = p.gy;
        }
      }
    }
    routePlayer(tx, ty) {
      const p = this.player;
      const from = p.step ? p.step.to : { gx: p.gx, gy: p.gy };
      if (!mayEnterPersonalOfficeTile(p.id, tx, ty)) {
        this.emit("vy-toast", { text: "This office is closed." });
        return false;
      }
      const path = findPath(from.gx, from.gy, tx, ty);
      if (path) {
        // Reject paths that enter a closed office mid-route
        for (const step of path) {
          if (!mayEnterPersonalOfficeTile(p.id, step.gx, step.gy)) {
            this.emit("vy-toast", { text: "This office is closed." });
            return false;
          }
        }
        p.path = path;
        this.target = { gx: tx, gy: ty, t: this.time };
        return true;
      }
      return false;
    }
    /**
     * Leave the current seat and step onto an adjacent walk tile.
     * Used by Zoom meeting mode "Stand up".
     */
    standUp() {
      const p = this.player;
      if (!p.sitting) return false;
      const st = seats[p.gx + "," + p.gy];
      if (st && st.occ === p.id) st.occ = null;
      p.sitting = null;
      const dirs = [
        [0, 1],
        [1, 0],
        [0, -1],
        [-1, 0],
        [1, 1],
        [-1, 1],
        [1, -1],
        [-1, -1],
      ];
      for (const [dx, dy] of dirs) {
        const tx = p.gx + dx;
        const ty = p.gy + dy;
        if (
          (isWalk(tx, ty) || seats[tx + "," + ty]) &&
          mayEnterPersonalOfficeTile(p.id, tx, ty) &&
          // Prefer open floor over another seat when standing
          isWalk(tx, ty)
        ) {
          p.path = [{ gx: tx, gy: ty }];
          this.target = null;
          return true;
        }
      }
      // Fallback: any adjacent seat/walk
      for (const [dx, dy] of dirs) {
        const tx = p.gx + dx;
        const ty = p.gy + dy;
        if ((isWalk(tx, ty) || seats[tx + "," + ty]) && mayEnterPersonalOfficeTile(p.id, tx, ty)) {
          p.path = [{ gx: tx, gy: ty }];
          this.target = null;
          return true;
        }
      }
      // No exit tile — re-apply sit so we don't leave a standing avatar on a seat.
      if (st) {
        p.sitting = { dir: st.dir };
        st.occ = p.id;
      }
      this.emitSitState();
      this.emit("vy-toast", { text: "Can't stand up here — walk to an open tile." });
      return false;
    }
    onClick(e) {
      const r = this.cv.getBoundingClientRect();
      const localX = e.clientX - r.left;
      const localY = e.clientY - r.top;
      const world = this.screenToWorld(localX, localY);
      const x = world.x - OX,
        y = world.y - MTOP;
      const gx = Math.floor((x / (TW / 2) + y / (TH / 2)) / 2),
        gy = Math.floor((y / (TH / 2) - x / (TW / 2)) / 2);
      if (gx < 0 || gy < 0 || gx >= COLS || gy >= ROWS) return;
      const seatKey = gx + "," + gy;
      const seat = seats[seatKey];
      if (seat) {
        // Occupied by someone else
        if (seat.occ && seat.occ !== this.player.id) {
          this.emit("vy-toast", { text: "That seat is taken." });
          return;
        }
        // Already sitting here — no-op (meeting mode stays open)
        if (this.player.sitting && this.player.gx === gx && this.player.gy === gy) {
          return;
        }
        this.routePlayer(gx, gy);
        return;
      }
      if (isWalk(gx, gy)) this.routePlayer(gx, gy);
    }
    emitSitState() {
      const p = this.player;
      const sitting = !!p.sitting;
      const z = zoneAt(Math.round(p.fx), Math.round(p.fy));
      const key = `${sitting ? 1 : 0}|${z.id}|${p.gx},${p.gy}`;
      if (key === this._lastSitKey) return;
      this._lastSitKey = key;
      this.emit("vy-sit", {
        sitting,
        gx: p.gx,
        gy: p.gy,
        zoneId: z.id,
        zoneName: z.name,
        private: !!(z.priv || z.personal),
        personal: !!z.personal,
        roomKind: z.roomKind || (z.personal ? "office" : z.priv ? "room" : null),
      });
    }
    onKey(e) {
      const tag = (document.activeElement && document.activeElement.tagName) || "";
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (document.activeElement && document.activeElement.isContentEditable)
      )
        return;
      const map = {
        ArrowUp: [-1, -1],
        ArrowDown: [1, 1],
        ArrowLeft: [-1, 1],
        ArrowRight: [1, -1],
        w: [-1, -1],
        s: [1, 1],
        a: [-1, 1],
        d: [1, -1],
      };
      // Actions: E = wave; 1–6 = catalog shortcuts. React listens and networks them.
      if (e.key === "e" || e.key === "E") {
        this.emit("vy-action-request", { action: "wave" });
        return;
      }
      // Zoom: +/= in, - out, 0 reset (works without focusing a control)
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        this.zoomIn();
        return;
      }
      if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        this.zoomOut();
        return;
      }
      if (e.key === "0") {
        e.preventDefault();
        this.resetZoom();
        return;
      }
      const shortcutAction = ACTION_SHORTCUT_MAP[e.key];
      if (shortcutAction) {
        this.emit("vy-action-request", { action: shortcutAction });
        return;
      }
      const dv = map[e.key];
      if (!dv) return;
      e.preventDefault();
      const p = this.player;
      if (p.step && p.step.t < 0.72) return;
      const from = p.step ? p.step.to : { gx: p.gx, gy: p.gy };
      let [dx, dy] = dv,
        tx = from.gx + dx,
        ty = from.gy + dy;
      const tgtOk = (x, y) =>
        (isWalk(x, y) || seats[x + "," + y]) && mayEnterPersonalOfficeTile(p.id, x, y);
      if (!tgtOk(tx, ty) || (!tgtOk(from.gx + dx, from.gy) && !tgtOk(from.gx, from.gy + dy))) {
        if (tgtOk(from.gx + dx, from.gy)) {
          tx = from.gx + dx;
          ty = from.gy;
        } else if (tgtOk(from.gx, from.gy + dy)) {
          tx = from.gx;
          ty = from.gy + dy;
        } else {
          if (
            (isWalk(tx, ty) || seats[tx + "," + ty]) &&
            !mayEnterPersonalOfficeTile(p.id, tx, ty)
          ) {
            this.emit("vy-toast", { text: "This office is closed." });
          }
          return;
        }
      }
      p.path = [{ gx: tx, gy: ty }];
      this.target = null;
    }
    // ---- simulation ----
    stepEntity(e, dt) {
      const speed = 3.4;
      if (!e.step && e.path.length) {
        const n = e.path.shift();
        e.step = { from: { gx: e.gx, gy: e.gy }, to: n, t: 0 };
        if (e.sitting) {
          const st = seats[e.gx + "," + e.gy];
          if (st && st.occ === e.id) st.occ = null;
          e.sitting = null;
        }
        const dx = n.gx - e.gx,
          dy = n.gy - e.gy;
        e.facing =
          dx === 1 && dy === 0
            ? "SE"
            : dx === -1 && dy === 0
              ? "NW"
              : dy === 1 && dx === 0
                ? "SW"
                : dy === -1 && dx === 0
                  ? "NE"
                  : dx === 1 && dy === 1
                    ? "SE"
                    : dx === -1 && dy === -1
                      ? "NE"
                      : dx === -1
                        ? "SW"
                        : "SE";
      }
      if (e.step) {
        e.step.t += dt * speed;
        e.ft += dt * speed * 2.2;
        e.frame = Math.floor(e.ft) % 2;
        if (e.step.t >= 1) {
          e.gx = e.step.to.gx;
          e.gy = e.step.to.gy;
          e.step = null;
          if (!e.path.length) {
            const st = seats[e.gx + "," + e.gy];
            if (st && (!st.occ || st.occ === e.id)) {
              e.sitting = { dir: st.dir };
              st.occ = e.id;
              // Map seat N/S/E/W → iso avatar facing quadrants
              const faceMap = { N: "NE", S: "SE", E: "SE", W: "SW" };
              e.facing = faceMap[st.dir] || "SE";
            }
          }
        }
      } else e.frame = 0;
      if (e.step) {
        const s = e.step;
        e.fx = s.from.gx + (s.to.gx - s.from.gx) * Math.min(1, s.t);
        e.fy = s.from.gy + (s.to.gy - s.from.gy) * Math.min(1, s.t);
      } else {
        e.fx = e.gx;
        e.fy = e.gy;
      }
      if (e.bubble && e.bubble.until < this.time) e.bubble = null;
      if (e.emote && e.emote.until < this.time) e.emote = null;
      if (e.action && e.action.until < this.time) e.action = null;
    }
    loop() {
      this.raf = requestAnimationFrame(() => this.loop());
      const now = performance.now();
      const dt = Math.min(0.05, (now - this.lastT) / 1000);
      this.lastT = now;
      this.time += dt;
      const remotes = this.remoteList();
      // Local player pathfinding
      this.stepEntity(this.player, dt);
      // Remote players: lerp toward network targets; infer sit pose on seats
      remotes.forEach((e) => {
        if (e.targetFx != null && e.targetFy != null) {
          const k = Math.min(1, dt * 10);
          e.fx += (e.targetFx - e.fx) * k;
          e.fy += (e.targetFy - e.fy) * k;
          e.gx = Math.round(e.fx);
          e.gy = Math.round(e.fy);
          e.frame = Math.abs(e.targetFx - e.fx) + Math.abs(e.targetFy - e.fy) > 0.02 ? 1 : 0;
        }
        const st = seats[e.gx + "," + e.gy];
        const still = e.frame === 0;
        if (st && still) {
          e.sitting = { dir: st.dir };
          const faceMap = { N: "NE", S: "SE", E: "SE", W: "SW" };
          e.facing = faceMap[st.dir] || e.facing || "SE";
        } else if (e.sitting) {
          e.sitting = null;
        }
        if (e.emote && e.emote.until < this.time) e.emote = null;
        if (e.action && e.action.until < this.time) e.action = null;
        if (e.bubble && e.bubble.until < this.time) e.bubble = null;
      });
      // Sit / stand events for local player
      this.emitSitState();
      // Room occupancy — private zone (company meeting, huddle, or personal office)
      const pRoomKey = this.privateZoneKey(this.player);
      const pInPrivate = pRoomKey !== null;
      const pSitting = !!this.player.sitting;
      const peersInSameRoom = remotes.filter((b) => {
        if (!pInPrivate || !pRoomKey) return false;
        return this.privateZoneKey(b) === pRoomKey;
      });
      // Zoom-style meeting mode: seated in a private zone (solo or with peers)
      const meetingMode = pSitting && pInPrivate;
      // "Live" = peers present in the same private zone (wall-screen animation)
      const peersLive = pInPrivate && peersInSameRoom.length > 0;
      const roomName = zoneAt(Math.round(this.player.fx), Math.round(this.player.fy)).name;
      const participantIds = peersInSameRoom.map((b) => b.id).sort();
      const sharingSig = peersInSameRoom.map((b) => `${b.id}:${b.sharing ? 1 : 0}`).join(",");
      const meetingSig = [
        meetingMode ? 1 : 0,
        peersLive ? 1 : 0,
        pInPrivate ? 1 : 0,
        pSitting ? 1 : 0,
        pRoomKey || "",
        participantIds.join(","),
        roomName,
        sharingSig,
      ].join("|");
      if (meetingSig !== this._lastMeetingSig) {
        this._lastMeetingSig = meetingSig;
        this.meeting.active = meetingMode;
        this.meeting.live = peersLive;
        this.meeting.wasInside = pInPrivate;
        this.meeting.roomId = pInPrivate ? pRoomKey : null;
        this.meeting.sitting = pSitting;
        const sharer = peersInSameRoom.find((b) => b.sharing);
        this.meeting.presenter = sharer
          ? sharer.name
          : peersInSameRoom[0]
            ? peersInSameRoom[0].name
            : "";
        this.meeting.topic = pInPrivate ? roomName : "Live huddle";
        this.emit("vy-meeting", {
          active: meetingMode,
          live: peersLive,
          sitting: pSitting,
          presenter: this.meeting.presenter,
          topic: this.meeting.topic,
          inside: pInPrivate,
          roomId: pInPrivate ? pRoomKey : null,
          personal: !!zoneAt(Math.round(this.player.fx), Math.round(this.player.fy)).personal,
          participants: peersInSameRoom.map((b) => ({
            id: b.id,
            name: b.name,
            camOn: !!b.camOn,
            micOn: b.micOn !== false,
            color: (b.colors && b.colors.shirt) || "#1E6CC7",
            sharing: !!b.sharing,
          })),
        });
      }
      // player zone + auto status (also re-evaluate when sit state changes)
      const p = this.player;
      const z = zoneAt(Math.round(p.fx), Math.round(p.fy));
      const sitFlag = p.sitting ? 1 : 0;
      if (z.id !== p.zone || sitFlag !== (p._statusSitFlag || 0)) {
        const zoneChanged = z.id !== p.zone;
        p.zone = z.id;
        p._statusSitFlag = sitFlag;
        if (zoneChanged) {
          this.emit("vy-zone", {
            id: z.id,
            name: z.name,
            priv: !!z.priv,
            personal: !!z.personal,
            doorStatus: z.doorStatus || null,
          });
        }
        let auto = null;
        // Seated in private room/office → In a Meeting; common private rooms always; focus → Focus
        if ((z.priv || z.personal) && p.sitting) auto = "meeting";
        else if (z.priv && !z.personal) auto = "meeting";
        else if (z.id === "focus") auto = "focus";
        const ns = auto || p.baseStatus;
        if (ns !== p.status) {
          p.status = ns;
          this.emit("vy-status", { status: ns, auto: !!auto });
        }
      }
      // Map transitions — fire once per standing on a transition tile (edge-triggered).
      if (!p.step && (!p.path || p.path.length === 0)) {
        const tgx = Math.round(p.fx);
        const tgy = Math.round(p.fy);
        const transitions = currentLayout.transitions || [];
        const hit = transitions.find((t) => t.gx === tgx && t.gy === tgy);
        if (hit) {
          if (this._lastTransitionId !== hit.id) {
            this._lastTransitionId = hit.id;
            this.emit("vyllage-map-transition", {
              toMapId: hit.toMapId,
              toSpawn: { gx: hit.toSpawn.gx, gy: hit.toSpawn.gy },
              label: hit.label || null,
              fromMapId: currentLayout.id || "campus",
              transitionId: hit.id,
            });
          }
        } else {
          this._lastTransitionId = null;
        }
      }
      // network pose tick
      if (this.time - (this.poseAt || 0) > 0.1) {
        this.poseAt = this.time;
        this.emit("vy-pose", this.getPose());
      }
      // camera + room focus zoom
      this.updateCamera(p, z, dt);
      this.render(remotes);
      this.drawMinimap(remotes);
      if (this.time - this.nearAt > 0.25) {
        this.nearAt = this.time;
        this.emit("vy-near", {
          people: remotes.map((b) => ({
            id: b.id,
            name: b.name,
            role: b.role,
            color: b.colors.shirt,
            status: b.status,
            camOn: b.camOn,
            talking: b.talking,
            vol: Math.round(this.volumeOf(b) * 100) / 100,
            zone: zoneAt(Math.round(b.fx), Math.round(b.fy)).name,
          })),
        });
      }
    }
    /**
     * Bounds of the enclosed space the player is in, or null in open floor.
     * Prefer the live zone rect (already resolved by zoneAt at the player tile)
     * so focus never latches onto a different pod.
     */
    roomFocusBounds(z) {
      if (!z || z.id === "commons") return null;
      if (z.personal) {
        return { x1: z.x1, y1: z.y1, x2: z.x2, y2: z.y2, strong: true };
      }
      if (z.priv) {
        return { x1: z.x1, y1: z.y1, x2: z.x2, y2: z.y2, strong: true };
      }
      if (z.id === "focus" || z.id === "lounge") {
        return { x1: z.x1, y1: z.y1, x2: z.x2, y2: z.y2, strong: false };
      }
      return null;
    }
    isoBounds(x1, y1, x2, y2) {
      // Inclusive tile corners in iso pixel space
      const corners = [iso(x1, y1), iso(x2 + 1, y1), iso(x1, y2 + 1), iso(x2 + 1, y2 + 1)];
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (const c of corners) {
        if (c.x < minX) minX = c.x;
        if (c.y < minY) minY = c.y;
        if (c.x > maxX) maxX = c.x;
        if (c.y > maxY) maxY = c.y;
      }
      return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
    }
    updateCamera(p, z, dt) {
      // Always resolve focus from the player's *current* tile — never cache room id.
      const liveZone = zoneAt(Math.round(p.fx), Math.round(p.fy));
      const focus = this.roomFocusBounds(liveZone);
      const pp = iso(p.fx + 0.5, p.fy + 0.5);
      let focusCx = pp.x,
        focusCy = pp.y;
      let autoTarget = 1;

      if (focus && this.vw > 0 && this.vh > 0) {
        const pad = focus.strong ? 1.2 : 1.4;
        const b = this.isoBounds(focus.x1, focus.y1, focus.x2, focus.y2);
        const fit = Math.min(this.vw / Math.max(1, b.w * pad), this.vh / Math.max(1, b.h * pad));
        // Strong rooms fill more of the screen; amenity zones only nudge in a little.
        const maxS = focus.strong ? 2.15 : 1.35;
        const minS = focus.strong ? 1.2 : 1.05;
        autoTarget = clamp(fit, minS, maxS);
        // Anchor mostly on the room's geometric center so the whole pod is framed.
        // Keep a light player bias so walking inside the room still feels responsive.
        const roomCx = (b.minX + b.maxX) / 2;
        const roomCy = (b.minY + b.maxY) / 2;
        const blend = focus.strong ? 0.78 : 0.4;
        focusCx = roomCx * blend + pp.x * (1 - blend);
        focusCy = roomCy * blend + pp.y * (1 - blend);
      }

      // userZoom multiplies auto room focus so you can pull out to path across HQ.
      this.view.autoTarget = autoTarget;
      const userZ = this.view.userZoom || 1;
      const targetScale = clamp(autoTarget * userZ, 0.32, 2.8);

      const k = 1 - Math.exp(-dt * 7);
      this.view.targetScale = targetScale;
      this.view.scale += (this.view.targetScale - this.view.scale) * k;

      // Render pipeline: translate(-cam) → translate(-vw/2) → scale(s) → translate(vw/2).
      // For a world point F to land at screen center, cam must be F - (vw/2, vh/2).
      // (Using vw/(2*s) was wrong and slid the zoom target to a different room.)
      const txx = focusCx - this.vw / 2;
      const tyy = focusCy - this.vh / 2;
      const s = this.view.scale;
      const maxX = Math.max(-40, WORLD_W - this.vw / Math.max(s, 0.5) + 40);
      const maxY = Math.max(-20, WORLD_H + MTOP + MBOT - this.vh / Math.max(s, 0.5) + 20);
      const cx = clamp(txx, -80, maxX);
      const cy = clamp(tyy, -40, maxY);
      this.cam.x += (cx - this.cam.x) * 0.14;
      this.cam.y += (cy - this.cam.y) * 0.14;
    }
    /** World pixel → screen CSS pixel (accounts for room zoom around viewport center). */
    worldToScreen(wx, wy) {
      const s = this.view.scale || 1;
      return {
        x: (wx - this.cam.x - this.vw / 2) * s + this.vw / 2,
        y: (wy - this.cam.y - this.vh / 2) * s + this.vh / 2,
      };
    }
    /** Screen CSS pixel → world pixel. */
    screenToWorld(sx, sy) {
      const s = this.view.scale || 1;
      return {
        x: (sx - this.vw / 2) / s + this.cam.x + this.vw / 2,
        y: (sy - this.vh / 2) / s + this.cam.y + this.vh / 2,
      };
    }
    initMinimap() {
      const wrap = document.createElement("div");
      wrap.className = "vy-minimap";
      wrap.title = "Mini-map — click to walk";
      const cv = document.createElement("canvas");
      cv.width = 168;
      cv.height = 132;
      cv.style.cssText = "display:block;width:168px;height:132px;cursor:pointer;";
      wrap.appendChild(cv);
      // Labels
      const label = document.createElement("div");
      label.className = "vy-minimap-label";
      label.textContent = "HQ MAP";
      wrap.appendChild(label);
      this.appendChild(wrap);
      this.minimap = { wrap, cv, ctx: cv.getContext("2d") };
      // pointer-events on wrap; convert clicks to grid walks
      wrap.style.pointerEvents = "auto";
      cv.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        const r = cv.getBoundingClientRect();
        const mx = e.clientX - r.left;
        const my = e.clientY - r.top;
        const pad = 6;
        const w = cv.width - pad * 2;
        const h = cv.height - pad * 2;
        const gx = Math.floor(((mx - pad) / w) * COLS);
        const gy = Math.floor(((my - pad) / h) * ROWS);
        if (gx >= 1 && gy >= 1 && gx < COLS - 1 && gy < ROWS - 1) {
          if (isWalk(gx, gy) || seats[gx + "," + gy]) this.routePlayer(gx, gy);
        }
      });
    }
    drawMinimap(remotes) {
      if (!this.minimap) return;
      const { cv, ctx } = this.minimap;
      const pad = 6;
      const w = cv.width - pad * 2;
      const h = cv.height - pad * 2;
      const sx = (gx) => pad + (gx / COLS) * w;
      const sy = (gy) => pad + (gy / ROWS) * h;
      const sw = (n) => (n / COLS) * w;
      const sh = (n) => (n / ROWS) * h;

      ctx.clearRect(0, 0, cv.width, cv.height);
      // frame
      ctx.fillStyle = "rgba(0, 15, 46, 0.88)";
      ctx.fillRect(0, 0, cv.width, cv.height);
      // floor
      ctx.fillStyle = "#1a2d52";
      ctx.fillRect(pad, pad, w, h);

      // common zones
      for (const z of commonZones) {
        ctx.fillStyle = z.priv ? "rgba(100, 71, 157, 0.45)" : "rgba(45, 155, 214, 0.28)";
        ctx.fillRect(sx(z.x1), sy(z.y1), sw(z.x2 - z.x1 + 1), sh(z.y2 - z.y1 + 1));
      }
      // personal offices
      for (const s of officeSlots) {
        const st = personalOfficeState.get(s.slotKey);
        const open = st?.doorStatus === "open";
        const owned = !!st?.ownerUserId;
        ctx.fillStyle = open
          ? "rgba(46, 168, 106, 0.55)"
          : owned
            ? "rgba(30, 108, 199, 0.5)"
            : "rgba(136, 148, 168, 0.35)";
        ctx.fillRect(sx(s.x1), sy(s.y1), sw(s.x2 - s.x1 + 1), sh(s.y2 - s.y1 + 1));
      }

      // viewport frustum (approx of current camera view in grid space)
      const p = this.player;
      // peers
      for (const b of remotes) {
        ctx.fillStyle = b.colors?.shirt || "#2D9BD6";
        ctx.beginPath();
        ctx.arc(sx(b.fx + 0.5), sy(b.fy + 0.5), 2.4, 0, 7);
        ctx.fill();
      }
      // you
      ctx.fillStyle = "#2D9BD6";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(sx(p.fx + 0.5), sy(p.fy + 0.5), 3.2, 0, 7);
      ctx.fill();
      ctx.stroke();

      // border
      ctx.strokeStyle = "rgba(45, 155, 214, 0.55)";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, cv.width - 1, cv.height - 1);
    }
    render(remotes) {
      const ctx = this.cv.getContext("2d");
      // Ground textures finished loading after the last bake → re-bake once (cheap;
      // bounded by the number of distinct floor tiles).
      if (this.floorDirty) {
        this.floorDirty = false;
        this.buildFloor();
      }
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, this.vw, this.vh);
      const s = this.view.scale || 1;
      ctx.save();
      // Zoom about viewport center
      ctx.translate(this.vw / 2, this.vh / 2);
      ctx.scale(s, s);
      ctx.translate(-this.vw / 2, -this.vh / 2);
      ctx.translate(-this.cam.x, -this.cam.y);
      // Background fill in world space covering the visible area
      const inv = 1 / s;
      ctx.fillStyle = "#D6DEEA";
      ctx.fillRect(this.cam.x - 20, this.cam.y - 20, this.vw * inv + 40, this.vh * inv + 40);
      // Floor is a hi-res supersampled bake drawn back at logical world size; filtered
      // sampling keeps it crisp and shimmer-free at any zoom. Sprites stay nearest.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(this.floor, 0, 0, WORLD_W, WORLD_H + MTOP + MBOT);
      ctx.imageSmoothingEnabled = false;
      const p = this.player;
      // proximity ring
      const pc = iso(p.fx + 0.5, p.fy + 0.5);
      ctx.save();
      ctx.strokeStyle = "rgba(30,108,199,0.45)";
      ctx.setLineDash([6, 5]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(pc.x, pc.y, (this.cfg.radius * TW) / 2, (this.cfg.radius * TH) / 2, 0, 0, 7);
      ctx.stroke();
      ctx.fillStyle = "rgba(45,155,214,0.05)";
      ctx.fill();
      ctx.restore();
      // click target
      if (this.target) {
        const t = iso(this.target.gx, this.target.gy),
          pulse = (Math.sin(this.time * 6) + 1) / 2;
        ctx.strokeStyle = `rgba(30,108,199,${0.4 + pulse * 0.5})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(t.x, t.y + 3);
        ctx.lineTo(t.x + TW / 2 - 4, t.y + TH / 2);
        ctx.lineTo(t.x, t.y + TH - 3);
        ctx.lineTo(t.x - TW / 2 + 4, t.y + TH / 2);
        ctx.closePath();
        ctx.stroke();
      }
      // depth-sorted draw
      const ents = [p, ...remotes];
      const allFurn = furn.concat(decorFurn);
      const items = allFurn
        .map((f) => ({
          k:
            f.kind === "platform"
              ? (f.gx + f.gy) * 100 + 90
              : f.kind === "frame"
                ? (f.gx + f.gy) * 100 + 30 + (f.depthBias || 0)
                : (f.gx + f.w - 1 + f.gy + f.d - 1) * 100 + 50 + (f.depthBias || 0),
          f,
        }))
        .concat(ents.map((e) => ({ k: (e.fx + e.fy) * 100 + 60, e })))
        .sort((a, b) => a.k - b.k);
      items.forEach((it) => {
        // Wall-screen bars use peer-present "live", not sit-mode "active"
        if (it.f) {
          drawPropShadow(ctx, it.f);
          drawFurniture(ctx, it.f, this.time * 1000, !!this.meeting.live);
        } else {
          const e = it.e,
            c = iso(e.fx + 0.5, e.fy + 0.5);
          ctx.fillStyle = "rgba(0,24,71,0.14)";
          ctx.beginPath();
          ctx.ellipse(c.x, c.y, 13, 5, 0, 0, 7);
          ctx.fill();
          const act =
            e.action && e.action.until > this.time
              ? { id: e.action.id, t: this.time - e.action.start }
              : null;
          drawAvatar(
            ctx,
            c.x,
            c.y + 2 - (e.step ? Math.abs(Math.sin(e.step.t * Math.PI * 2)) * 2.2 : 0),
            e.facing,
            e.frame,
            e.colors,
            { sitting: !!e.sitting, action: act, walk: e.step ? e.step.t : null },
          );
        }
      });
      ctx.restore();
      // Vignette — live screen-space radial gradient (crisp; no upscale banding).
      {
        const cx = this.vw / 2,
          cy = this.vh / 2;
        const r = Math.hypot(this.vw, this.vh) * 0.62;
        const vg = ctx.createRadialGradient(cx, cy, r * 0.35, cx, cy, r);
        vg.addColorStop(0, "rgba(0,24,71,0)");
        vg.addColorStop(1, "rgba(0,24,71,0.16)");
        ctx.fillStyle = vg;
        ctx.fillRect(0, 0, this.vw, this.vh);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.syncDOM(remotes);
    }
    /**
     * Screen-space Y offset from entity foot anchor to a point above the avatar head.
     * Uses world→screen so room zoom keeps labels glued above the drawn sprite.
     * Matches drawAvatar foot/head with u=2 (see render path: fy = isoY + 2).
     */
    overlayHeadOffset(e, extraLift = 8) {
      const foot = iso(e.fx + 0.5, e.fy + 0.5);
      const u = 2;
      // Same local Y math as drawAvatar (standing vs sit).
      const headTopLocal = e.sitting
        ? -7 * u + -24 * u // sit: fy lowered, head hy≈-20 plus hair
        : -32 * u; // stand: hy=-27 plus hair/hat clearance
      const footDrawY = foot.y + 2;
      const headWorldY = footDrawY + headTopLocal - extraLift;
      const footScr = this.worldToScreen(foot.x, foot.y);
      const headScr = this.worldToScreen(foot.x, headWorldY);
      return headScr.y - footScr.y;
    }
    syncDOM(remotes) {
      const p = this.player;
      [p, ...remotes].forEach((e) => {
        const d = this.dom[e.id];
        if (!d) return;
        const c = iso(e.fx + 0.5, e.fy + 0.5);
        const scr = this.worldToScreen(c.x, c.y);
        const x = scr.x,
          y = scr.y;
        const vis = x > -140 && x < this.vw + 140 && y > -160 && y < this.vh + 160;
        d.w.style.display = vis ? "block" : "none";
        if (!vis) return;
        d.w.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
        d.w.style.zIndex = Math.round(y) + 200;
        // Bottom of name tag / bubble sits at head top (transform-origin bottom).
        const headY = this.overlayHeadOffset(e, 10);
        const vol = e.isRemote ? this.volumeOf(e) : 1;
        const status = e.status;
        // label
        const showLab = this.cfg.showNames && !e.bubble;
        d.lab.style.display = showLab ? "flex" : "none";
        if (showLab) {
          d.lab.style.transformOrigin = "50% 100%";
          d.lab.style.transform = `translate(-50%, ${Math.round(headY)}px)`;
          d.lab.style.opacity = e.isRemote ? 0.45 + 0.55 * vol : 1;
          d.labName.textContent = e.isRemote
            ? e.name.split(" ")[0]
            : e.name === "You"
              ? "You"
              : e.name;
          d.labDot.style.background = STATUS_C[status] || STATUS_C.available;
        }
        // bubble
        if (e.bubble) {
          d.bub.style.display = "block";
          if (d.bub.textContent !== e.bubble.text) d.bub.textContent = e.bubble.text;
          d.bub.style.transformOrigin = "50% 100%";
          d.bub.style.transform = `translate(-50%, ${Math.round(headY - 4)}px)`;
          d.bub.style.opacity = e.isRemote ? 0.25 + 0.75 * Math.max(vol, 0.15) : 1;
        } else d.bub.style.display = "none";
        // cam tile
        const showCam = e.camOn && (e.isRemote ? vol > 0.08 : true) && status !== "focus";
        d.cam.style.display = showCam ? "block" : "none";
        if (showCam) {
          const sc = e.isRemote ? 0.72 + 0.34 * vol : 1;
          const lift = e.bubble ? 46 : 0;
          d.cam.style.transformOrigin = "50% 100%";
          d.cam.style.transform = `translate(-50%, ${Math.round(headY - 8 - lift)}px) scale(${sc.toFixed(2)})`;
          d.cam.style.opacity = e.isRemote ? 0.4 + 0.6 * vol : 1;
          d.cam.style.borderColor = STATUS_C[status] || STATUS_C.available;
          const talking = e.talking && (e.isRemote ? vol > 0.05 : e.micOn);
          const micOff = !e.isRemote && !e.micOn;
          d.ind.innerHTML = micOff
            ? '<span class="vy-mut"></span>'
            : talking
              ? '<span class="vy-eq"><i></i><i></i><i></i></span>'
              : "";
        }
        // emote
        if (e.emote) {
          d.emo.style.display = "block";
          d.emo.textContent = e.emote.txt;
          d.emo.style.transformOrigin = "50% 100%";
          const emoLift = e.bubble ? 52 : 28;
          d.emo.style.transform = `translate(-50%, ${Math.round(headY - emoLift)}px)`;
        } else d.emo.style.display = "none";
      });
    }
  }
  customElements.define("vyllage-world", VyWorld);
}
